import React, { useState, useEffect } from 'react';
import { parseLocalDate, formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, DollarSign, Award, Calendar, ArrowRight, Banknote, ChevronLeft, ChevronRight, Target, Users, Building2, Zap, PieChart as PieChartIcon, BarChart2, Trophy, FileText, Clock, AlertTriangle, MapPin, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { calculateLoanCommission, calculateProjectedCommission, getCurrentTier } from '../utils/commissionTiers';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isProcessor } = useAuth();
  const userBrokerType = user?.broker_type || 'senior';
  const [loans, setLoans] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState('mine');
  const [showOfficerDropdown, setShowOfficerDropdown] = useState(false); // 'mine', 'all', or officer id
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'ytd'
  const [pullThroughPeriod, setPullThroughPeriod] = useState('lifetime');
  const [stats, setStats] = useState({
    monthEarnings: 0,
    monthClosedLoans: 0,
    projectedCommission: 0,
    inProcessCount: 0,
    monthVolume: 0,
    currentTier: null,
    recentLoans: []
  });
  const [ytdStats, setYtdStats] = useState({
    earnings: 0,
    closedLoans: 0,
    inProcessCount: 0,
    volume: 0,
    projectedCommission: 0,
    recentLoans: []
  });
  const [kpis, setKpis] = useState({
    allTimeLoans: 0,
    allTimeVolume: 0,
    avgLoanSize: 0,
    selfGenLoans: 0,
    companyLoans: 0,
    selfGenEarnings: 0,
    companyEarnings: 0,
    leadSourceBreakdown: [],
    lenderBreakdown: [],
    monthlyTrends: [],
    inProcessLoans: 0,
    inProcessVolume: 0,
    withdrawnLoans: 0,
    pullThrough: {
      lifetime: 0,
      ytd: 0,
      quarterly: 0,
      last30Days: 0
    },
    selfGenByPeriod: {
      lifetime: { loans: 0, earnings: 0, loanDetails: [] },
      ytd: { loans: 0, earnings: 0, loanDetails: [] },
      quarterly: { loans: 0, earnings: 0, loanDetails: [] },
      last30Days: { loans: 0, earnings: 0, loanDetails: [] }
    },
    companyByPeriod: {
      lifetime: { loans: 0, earnings: 0, loanDetails: [] },
      ytd: { loans: 0, earnings: 0, loanDetails: [] },
      quarterly: { loans: 0, earnings: 0, loanDetails: [] },
      last30Days: { loans: 0, earnings: 0, loanDetails: [] }
    }
  });
  const [selfGenPeriod, setSelfGenPeriod] = useState('lifetime');
  const [companyPeriod, setCompanyPeriod] = useState('lifetime');
  const [showDetailDialog, setShowDetailDialog] = useState(null); // 'selfGen' or 'company'
  const [leaderboard, setLeaderboard] = useState([]);
  const [showAgedLoansDialog, setShowAgedLoansDialog] = useState(false);
  const [agedLoansData, setAgedLoansData] = useState([]);
  const [salesViewMode, setSalesViewMode] = useState('monthly'); // 'monthly' or 'ytd'
  const [processingViewMode, setProcessingViewMode] = useState('monthly'); // 'monthly' or 'ytd'
  const [statusPickerDialog, setStatusPickerDialog] = useState({ open: false, loanId: null, currentStatus: '' });
  const [fundingDateDialog, setFundingDateDialog] = useState({ open: false, loanId: null, fundingDate: '', closingDate: '' });
  const [showWithdrawnDialog, setShowWithdrawnDialog] = useState(false);

  const handleDashboardStatusClick = (loan) => {
    setStatusPickerDialog({ open: true, loanId: loan.id, currentStatus: loan.status || 'In Process' });
  };

  const handleDashboardStatusSelect = async (newStatus) => {
    const { loanId, currentStatus } = statusPickerDialog;
    if (newStatus === currentStatus) {
      setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' });
      return;
    }

    // If changing to Closed, show funding date dialog
    if (newStatus === 'Closed') {
      const loan = loans.find(l => l.id === loanId);
      let defaultFundingDate = loan?.fundingDate || loan?.funding_date;
      if (!defaultFundingDate && loan?.date) {
        const [y, m, d] = loan.date.split('-').map(Number);
        const fundDate = new Date(y, m - 1, d + 3);
        defaultFundingDate = `${fundDate.getFullYear()}-${String(fundDate.getMonth() + 1).padStart(2, '0')}-${String(fundDate.getDate()).padStart(2, '0')}`;
      }
      setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' });
      setFundingDateDialog({
        open: true,
        loanId,
        fundingDate: defaultFundingDate || new Date().toISOString().split('T')[0],
        closingDate: loan?.date || new Date().toISOString().split('T')[0]
      });
      return;
    }

    try {
      await axios.put(`${API}/api/loans/${loanId}`, { status: newStatus });
      setLoans(loans.map(l => l.id === loanId ? { ...l, status: newStatus } : l));
      setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating loan status. Please try again.');
    }
  };

  const handleDashboardFundingSubmit = async () => {
    const { loanId, fundingDate, closingDate } = fundingDateDialog;
    if (!closingDate || !fundingDate) {
      alert('Please enter both the Closing Date and Funding Date to proceed.');
      return;
    }
    try {
      await axios.put(`${API}/api/loans/${loanId}`, {
        status: 'Closed',
        funding_date: fundingDate,
        date: closingDate
      });
      setLoans(loans.map(l => l.id === loanId ? { ...l, status: 'Closed', fundingDate: fundingDate, date: closingDate } : l));
      setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' });
    } catch (error) {
      console.error('Error updating loan:', error);
      alert('Error updating loan. Please try again.');
    }
  };

  // Fetch monthly or YTD leaderboard
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        let url;
        if (viewMode === 'ytd') {
          url = `${API}/api/leaderboard?year=${selectedMonth.getFullYear()}`;
        } else {
          const monthKey = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
          url = `${API}/api/leaderboard?month=${monthKey}`;
        }
        const response = await axios.get(url);
        setLeaderboard(response.data.leaderboard || []);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        setLeaderboard([]);
      }
    };
    fetchLeaderboard();
  }, [selectedMonth, viewMode]);

  // Fetch loan officers list for admin dropdown
  useEffect(() => {
    if (isAdmin) {
      const fetchOfficers = async () => {
        try {
          const response = await axios.get(`${API}/api/admin/loan-officers`);
          setLoanOfficers(response.data);
        } catch (error) {
          console.error('Error fetching loan officers:', error);
        }
      };
      fetchOfficers();
    }
  }, [isAdmin]);

  // Format month for display
  const formatMonth = (date) => {
    return parseLocalDate(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedMonth(newDate);
  };

  // Load loans from API
  useEffect(() => {
    const fetchLoans = async () => {
      try {
        let url = `${API}/api/loans`;
        if (isAdmin) {
          // Check if selected user is a processor
          const isViewingProcessor = selectedOfficer !== 'mine' && selectedOfficer !== 'all' && 
            loanOfficers.find(o => o.id === selectedOfficer)?.role === 'processor';
          if (selectedOfficer === 'all' || selectedOfficer === 'all_processing' || isViewingProcessor) {
            // Fetch all loans when viewing company-wide or a processor's pipeline
            url += '?user_id=all';
          } else if (selectedOfficer !== 'mine') {
            url += `?user_id=${selectedOfficer}`;
          }
        }
        // Processors automatically get all loans from backend
        const response = await axios.get(url);
        // Transform API response to match frontend format
        const transformedLoans = response.data.map(loan => ({
          id: loan.id,
          userId: loan.user_id,
          userName: loan.user_name,
          clientName: loan.client_name,
          loanNumber: loan.loan_number,
          loanAmount: loan.loan_amount,
          baseCommissionRate: loan.base_commission_rate,
          commissionType: loan.commission_type || 'percent',
          lender: loan.lender,
          leadSource: loan.lead_source,
          processor: loan.processor,
          isSelfGenerated: loan.is_self_generated,
          status: loan.status,
          date: loan.date,
          submissionDate: loan.submission_date,
          reimbursement: loan.reimbursement,
          cure: loan.cure,
          state: loan.state,
          loanType: loan.loan_type,
          fundingDate: loan.funding_date
        }));
        setLoans(transformedLoans);
      } catch (error) {
        console.error('Error fetching loans:', error);
      }
    };
    fetchLoans();
  }, [isAdmin, selectedOfficer, loanOfficers]);

  // Recalculate stats when selectedMonth or loans change
  useEffect(() => {
    if (loans.length > 0) {
      calculateStats(loans, selectedMonth);
    } else {
      // Reset stats if no loans
      setStats({
        monthEarnings: 0,
        monthClosedLoans: 0,
        projectedCommission: 0,
        inProcessCount: 0,
        monthVolume: 0,
        currentTier: getCurrentTier(0, userBrokerType),
        recentLoans: []
      });
    }
  }, [loans, selectedMonth, user?.broker_type]);

  const calculateStats = (loanData, targetDate) => {
    const targetMonth = targetDate.getMonth();
    const targetYear = targetDate.getFullYear();

    // Filter loans for selected month
    const selectedMonthLoans = loanData.filter(l => {
      const loanDate = parseLocalDate(l.date);
      return loanDate.getMonth() === targetMonth && loanDate.getFullYear() === targetYear;
    });

    // Separate closed and in-process loans
    const selectedMonthClosedLoans = selectedMonthLoans.filter(l => l.status === 'Closed');
    const selectedMonthInProcessLoans = selectedMonthLoans.filter(l => 
      l.status === 'In Process' || l.status === 'Pending'
    );

    // Calculate commission for closed loans
    let monthEarnings = 0;
    const monthClosedCount = selectedMonthClosedLoans.length;
    
    selectedMonthClosedLoans.forEach(loan => {
      const baseCommission = calculateLoanCommission(loan, monthClosedCount, userBrokerType);
      const reimbursement = parseFloat(loan.reimbursement || 0);
      const cure = parseFloat(loan.cure || 0);
      monthEarnings += baseCommission + reimbursement - cure;
    });

    // Calculate PROJECTED commission from in-process loans
    // Projected tier is based on closed + in-process combined
    let projectedCommission = 0;
    const projectedTotalLoans = monthClosedCount + selectedMonthInProcessLoans.length;
    
    selectedMonthInProcessLoans.forEach(loan => {
      // Calculate what the commission would be if this loan closes
      // Use the projected total count for tier calculation
      const baseCommission = calculateProjectedCommission(loan, projectedTotalLoans, userBrokerType);
      const reimbursement = parseFloat(loan.reimbursement || 0);
      const cure = parseFloat(loan.cure || 0);
      projectedCommission += baseCommission + reimbursement - cure;
    });

    // Calculate month's volume (closed loan amounts only)
    const monthVolume = selectedMonthClosedLoans.reduce((sum, loan) => sum + loan.loanAmount, 0);

    // Get current tier based on this month's closed loans
    const currentTier = getCurrentTier(monthClosedCount, userBrokerType);

    // Get recent closed loans for this month (sorted by date)
    const recentLoans = selectedMonthClosedLoans
      .sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date))
      .slice(0, 5);

    setStats({
      monthEarnings,
      monthClosedLoans: monthClosedCount,
      projectedCommission,
      inProcessCount: selectedMonthInProcessLoans.length,
      monthVolume,
      currentTier,
      recentLoans
    });

    // Calculate YTD stats
    const ytdLoans = loanData.filter(l => {
      const loanDate = parseLocalDate(l.date);
      return loanDate.getFullYear() === targetYear;
    });
    const ytdClosed = ytdLoans.filter(l => l.status === 'Closed');
    const ytdInProcess = ytdLoans.filter(l => l.status === 'In Process' || l.status === 'Pending');

    // Group YTD closed by month for proper tier-based commission
    const ytdByMonth = {};
    ytdClosed.forEach(loan => {
      const loanDate = parseLocalDate(loan.date);
      const mk = `${loanDate.getFullYear()}-${String(loanDate.getMonth() + 1).padStart(2, '0')}`;
      if (!ytdByMonth[mk]) ytdByMonth[mk] = [];
      ytdByMonth[mk].push(loan);
    });

    let ytdEarnings = 0;
    Object.values(ytdByMonth).forEach(mLoans => {
      const mCount = mLoans.length;
      mLoans.forEach(loan => {
        const baseCommission = calculateLoanCommission(loan, mCount, userBrokerType);
        const reimbursement = parseFloat(loan.reimbursement || 0);
        const cure = parseFloat(loan.cure || 0);
        ytdEarnings += baseCommission + reimbursement - cure;
      });
    });

    let ytdProjected = 0;
    ytdInProcess.forEach(loan => {
      const baseCommission = calculateProjectedCommission(loan, ytdInProcess.length, userBrokerType);
      const reimbursement = parseFloat(loan.reimbursement || 0);
      const cure = parseFloat(loan.cure || 0);
      ytdProjected += baseCommission + reimbursement - cure;
    });

    const ytdVolume = ytdClosed.reduce((sum, loan) => sum + loan.loanAmount, 0);
    const ytdRecentLoans = ytdClosed
      .sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date))
      .slice(0, 5);

    setYtdStats({
      earnings: ytdEarnings,
      closedLoans: ytdClosed.length,
      inProcessCount: ytdInProcess.length,
      volume: ytdVolume,
      projectedCommission: ytdProjected,
      recentLoans: ytdRecentLoans
    });
  };

  // Calculate all-time KPIs
  const calculateKpis = (loanData) => {
    const closedLoans = loanData.filter(l => l.status === 'Closed');
    const inProcessLoansData = loanData.filter(l => l.status === 'In Process' || l.status === 'Pending');
    const withdrawnLoansData = loanData.filter(l => l.status === 'Withdrawn');
    
    // In Process metrics
    const inProcessLoans = inProcessLoansData.length;
    const inProcessVolume = inProcessLoansData.reduce((sum, loan) => sum + (loan.loanAmount || 0), 0);
    const withdrawnLoans = withdrawnLoansData.length;
    
    // Calculate Pull Through and Fallout by time periods
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const startOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const calculatePullThroughForPeriod = (startDate) => {
      const periodClosed = closedLoans.filter(l => parseLocalDate(l.date) >= startDate);
      const periodWithdrawn = withdrawnLoansData.filter(l => parseLocalDate(l.date) >= startDate);
      const total = periodClosed.length + periodWithdrawn.length;
      return {
        pullThrough: total > 0 ? (periodClosed.length / total) * 100 : 0,
        fallout: total > 0 ? (periodWithdrawn.length / total) * 100 : 0
      };
    };
    
    // Calculate for each time period
    const lifetimeTotal = closedLoans.length + withdrawnLoans;
    const pullThroughLifetime = lifetimeTotal > 0 ? (closedLoans.length / lifetimeTotal) * 100 : 0;
    const falloutLifetime = lifetimeTotal > 0 ? (withdrawnLoans / lifetimeTotal) * 100 : 0;
    
    const ytdMetrics = calculatePullThroughForPeriod(startOfYear);
    const quarterlyMetrics = calculatePullThroughForPeriod(startOfQuarter);
    const last30DaysMetrics = calculatePullThroughForPeriod(last30Days);
    
    // Group closed loans by month for proper commission calculation
    const loansByMonth = {};
    closedLoans.forEach(loan => {
      const loanDate = parseLocalDate(loan.date);
      const monthKey = `${loanDate.getFullYear()}-${String(loanDate.getMonth() + 1).padStart(2, '0')}`;
      if (!loansByMonth[monthKey]) {
        loansByMonth[monthKey] = [];
      }
      loansByMonth[monthKey].push(loan);
    });

    // Calculate all-time earnings with proper tier calculation per month
    let selfGenEarnings = 0;
    let companyEarnings = 0;
    
    // Also track earnings by period
    const selfGenByPeriod = {
      lifetime: { loans: 0, earnings: 0, loanDetails: [] },
      ytd: { loans: 0, earnings: 0, loanDetails: [] },
      quarterly: { loans: 0, earnings: 0, loanDetails: [] },
      last30Days: { loans: 0, earnings: 0, loanDetails: [] }
    };
    const companyByPeriod = {
      lifetime: { loans: 0, earnings: 0, loanDetails: [] },
      ytd: { loans: 0, earnings: 0, loanDetails: [] },
      quarterly: { loans: 0, earnings: 0, loanDetails: [] },
      last30Days: { loans: 0, earnings: 0, loanDetails: [] }
    };
    
    Object.values(loansByMonth).forEach(monthLoans => {
      const monthClosedCount = monthLoans.length;
      monthLoans.forEach(loan => {
        const commission = calculateLoanCommission(loan, monthClosedCount, userBrokerType);
        const reimbursement = parseFloat(loan.reimbursement || 0);
        const cure = parseFloat(loan.cure || 0);
        const total = commission + reimbursement - cure;
        const loanDate = parseLocalDate(loan.date);
        
        if (loan.isSelfGenerated) {
          selfGenEarnings += total;
          selfGenByPeriod.lifetime.loans++;
          selfGenByPeriod.lifetime.earnings += total;
          selfGenByPeriod.lifetime.loanDetails.push({ ...loan, commission: total });
          if (loanDate >= startOfYear) {
            selfGenByPeriod.ytd.loans++;
            selfGenByPeriod.ytd.earnings += total;
            selfGenByPeriod.ytd.loanDetails.push({ ...loan, commission: total });
          }
          if (loanDate >= startOfQuarter) {
            selfGenByPeriod.quarterly.loans++;
            selfGenByPeriod.quarterly.earnings += total;
            selfGenByPeriod.quarterly.loanDetails.push({ ...loan, commission: total });
          }
          if (loanDate >= last30Days) {
            selfGenByPeriod.last30Days.loans++;
            selfGenByPeriod.last30Days.earnings += total;
            selfGenByPeriod.last30Days.loanDetails.push({ ...loan, commission: total });
          }
        } else {
          companyEarnings += total;
          companyByPeriod.lifetime.loans++;
          companyByPeriod.lifetime.earnings += total;
          companyByPeriod.lifetime.loanDetails.push({ ...loan, commission: total });
          if (loanDate >= startOfYear) {
            companyByPeriod.ytd.loans++;
            companyByPeriod.ytd.earnings += total;
            companyByPeriod.ytd.loanDetails.push({ ...loan, commission: total });
          }
          if (loanDate >= startOfQuarter) {
            companyByPeriod.quarterly.loans++;
            companyByPeriod.quarterly.earnings += total;
            companyByPeriod.quarterly.loanDetails.push({ ...loan, commission: total });
          }
          if (loanDate >= last30Days) {
            companyByPeriod.last30Days.loans++;
            companyByPeriod.last30Days.earnings += total;
            companyByPeriod.last30Days.loanDetails.push({ ...loan, commission: total });
          }
        }
      });
    });

    // All-time volume
    const allTimeVolume = closedLoans.reduce((sum, loan) => sum + loan.loanAmount, 0);
    
    // Average loan size
    const avgLoanSize = closedLoans.length > 0 ? allTimeVolume / closedLoans.length : 0;
    
    // Self-gen vs company count
    const selfGenLoans = closedLoans.filter(l => l.isSelfGenerated).length;
    const companyLoans = closedLoans.filter(l => !l.isSelfGenerated).length;
    
    // Lead source breakdown
    const leadSourceMap = {};
    closedLoans.forEach(loan => {
      const source = loan.leadSource || 'Unknown';
      if (!leadSourceMap[source]) {
        leadSourceMap[source] = { count: 0, volume: 0, earnings: 0 };
      }
      leadSourceMap[source].count++;
      leadSourceMap[source].volume += loan.loanAmount;
    });
    
    // Calculate earnings per lead source
    Object.keys(loansByMonth).forEach(monthKey => {
      const monthLoans = loansByMonth[monthKey];
      const monthClosedCount = monthLoans.length;
      monthLoans.forEach(loan => {
        const source = loan.leadSource || 'Unknown';
        const commission = calculateLoanCommission(loan, monthClosedCount, userBrokerType);
        const reimbursement = parseFloat(loan.reimbursement || 0);
        const cure = parseFloat(loan.cure || 0);
        leadSourceMap[source].earnings += commission + reimbursement - cure;
      });
    });
    
    const leadSourceBreakdown = Object.entries(leadSourceMap)
      .map(([source, data]) => ({ source, ...data }))
      .sort((a, b) => b.count - a.count);
    
    // Lender breakdown
    const lenderMap = {};
    closedLoans.forEach(loan => {
      const lender = loan.lender || 'Unknown';
      if (!lenderMap[lender]) {
        lenderMap[lender] = { count: 0, volume: 0 };
      }
      lenderMap[lender].count++;
      lenderMap[lender].volume += loan.loanAmount;
    });
    
    const lenderBreakdown = Object.entries(lenderMap)
      .map(([lender, data]) => ({ lender, ...data }))
      .sort((a, b) => b.count - a.count);
    
    // Monthly trends (Jan to Dec of current year)
    const monthlyTrends = [];
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 12; i++) {
      const d = new Date(currentYear, i, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthName = d.toLocaleDateString('en-US', { month: 'short' });
      const monthLoans = loansByMonth[monthKey] || [];
      
      let monthEarnings = 0;
      monthLoans.forEach(loan => {
        const commission = calculateLoanCommission(loan, monthLoans.length, userBrokerType);
        const reimbursement = parseFloat(loan.reimbursement || 0);
        const cure = parseFloat(loan.cure || 0);
        monthEarnings += commission + reimbursement - cure;
      });
      
      monthlyTrends.push({
        month: monthName,
        loans: monthLoans.length,
        volume: monthLoans.reduce((sum, l) => sum + l.loanAmount, 0),
        earnings: monthEarnings
      });
    }
    
    // Add decline indicator for each month (compared to previous month)
    for (let i = 0; i < monthlyTrends.length; i++) {
      if (i === 0) {
        monthlyTrends[i].isDecline = false;
      } else {
        monthlyTrends[i].isDecline = monthlyTrends[i].loans < monthlyTrends[i - 1].loans;
      }
    }

    setKpis({
      allTimeLoans: closedLoans.length,
      allTimeVolume,
      avgLoanSize,
      selfGenLoans,
      companyLoans,
      selfGenEarnings,
      companyEarnings,
      leadSourceBreakdown,
      lenderBreakdown,
      monthlyTrends,
      inProcessLoans,
      inProcessVolume,
      withdrawnLoans,
      pullThrough: {
        lifetime: pullThroughLifetime,
        ytd: ytdMetrics.pullThrough,
        quarterly: quarterlyMetrics.pullThrough,
        last30Days: last30DaysMetrics.pullThrough
      },
      selfGenByPeriod,
      companyByPeriod
    });
  };

  // Recalculate KPIs when loans change
  useEffect(() => {
    if (loans.length > 0) {
      calculateKpis(loans);
    }
  }, [loans, user?.broker_type]);

  // Sync user's broker type to localStorage on mount
  useEffect(() => {
    if (user?.broker_type) {
      localStorage.setItem('kinfolk_broker_type', user.broker_type);
    }
  }, [user?.broker_type]);

  const getBrokerTypeDisplay = (type) => {
    const types = {
      'equity': 'Equity Partner',
      'founding': 'Founding Broker',
      'executive': 'Executive Broker',
      'senior': 'Senior Broker'
    };
    return types[type] || 'Select Broker Type';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Determine if admin is viewing a processor's profile
  const selectedUserRole = loanOfficers.find(o => o.id === selectedOfficer)?.role;
  const viewingProcessor = selectedOfficer !== 'mine' && selectedOfficer !== 'all' && selectedOfficer !== 'all_processing' && selectedUserRole === 'processor';
  const viewingProcessorName = viewingProcessor ? loanOfficers.find(o => o.id === selectedOfficer)?.name : '';

  // Split officers and processors for dropdown groups
  const officersList = loanOfficers.filter(o => o.role !== 'processor');
  const processorsList = loanOfficers.filter(o => o.role === 'processor');

  const brokerType = user?.broker_type;

  // ── PROCESSOR DASHBOARD ──
  if (isProcessor) {
    const processorName = user?.name || '';
    
    // Filter loans assigned to this processor
    const myAssignedLoans = loans.filter(l => l.processor === processorName);
    
    // Month-filtered assigned loans
    const targetMonth = selectedMonth.getMonth();
    const targetYear = selectedMonth.getFullYear();
    const monthAssigned = myAssignedLoans.filter(l => {
      const d = parseLocalDate(l.date);
      return d.getMonth() === targetMonth && d.getFullYear() === targetYear;
    });

    // YTD-filtered assigned loans
    const ytdAssigned = myAssignedLoans.filter(l => {
      const d = parseLocalDate(l.date);
      return d.getFullYear() === targetYear;
    });

    // Use viewMode to pick active dataset
    const activeAssigned = viewMode === 'ytd' ? ytdAssigned : monthAssigned;
    
    // Stats
    const assignedInProcess = activeAssigned.filter(l => l.status === 'In Process' || l.status === 'Pending');
    const assignedClosed = activeAssigned.filter(l => l.status === 'Closed');
    const assignedWithdrawn = activeAssigned.filter(l => l.status === 'Withdrawn');
    const assignedVolume = activeAssigned.reduce((sum, l) => sum + (l.loanAmount || 0), 0);
    const closedVolume = assignedClosed.reduce((sum, l) => sum + (l.loanAmount || 0), 0);
    
    // All-time stats
    const allTimeActive = myAssignedLoans.filter(l => l.status === 'In Process' || l.status === 'Pending').length;
    const allTimeClosed = myAssignedLoans.filter(l => l.status === 'Closed').length;
    const allTimeVolume = myAssignedLoans.filter(l => l.status === 'Closed').reduce((sum, l) => sum + (l.loanAmount || 0), 0);
    
    // Company-wide month stats (no commission)
    const allMonthLoans = loans.filter(l => {
      const d = parseLocalDate(l.date);
      if (viewMode === 'ytd') {
        return d.getFullYear() === targetYear;
      }
      return d.getMonth() === targetMonth && d.getFullYear() === targetYear;
    });
    const companyMonthClosed = allMonthLoans.filter(l => l.status === 'Closed');
    const companyMonthVolume = companyMonthClosed.reduce((sum, l) => sum + (l.loanAmount || 0), 0);

    return (
      <div className="w-full max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-6" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="processor-dashboard">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-2xl lg:text-3xl font-semibold text-white">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
            </h1>
            <p className="text-gray-400 text-sm">Your processing pipeline and workload overview</p>
          </div>
          <Badge className="bg-teal-900/50 text-teal-400 border border-teal-700 text-sm px-3 py-1.5">
            Processor
          </Badge>
        </div>

        {/* Month / YTD Selector */}
        <Card className="bg-[#111] border-none rounded-xl">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              {viewMode === 'month' ? (
                <>
                  <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{formatMonth(selectedMonth)}</div>
                    <p className="text-xs text-gray-500 mt-1">Processing period</p>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => navigateMonth(1)} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="icon" onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() - 1, selectedMonth.getMonth(), 1))} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{selectedMonth.getFullYear()}</div>
                    <p className="text-xs text-gray-500 mt-1">Jan – Dec {selectedMonth.getFullYear()}</p>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() + 1, selectedMonth.getMonth(), 1))} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </>
              )}
            </div>
            {/* Monthly / YTD Toggle */}
            <div className="flex justify-center mt-3">
              <div className="bg-[#0a0a0a] rounded-lg p-1 flex gap-1">
                <button
                  onClick={() => setViewMode('month')}
                  data-testid="processor-view-mode-month"
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'month'
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setViewMode('ytd')}
                  data-testid="processor-view-mode-ytd"
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'ytd'
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  YTD
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* My Pipeline Stats */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-teal-500" />
            My Pipeline
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[24px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-teal-500" data-testid="processor-total-assigned">
              <CardContent className="p-[24px]">
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-400">Assigned to Me</span>
                  <div className="text-2xl font-bold text-teal-400">{activeAssigned.length}</div>
                  <p className="text-xs text-gray-500">{viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : 'This month'}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-amber-500" data-testid="processor-in-process">
              <CardContent className="p-[24px]">
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-400">In Process</span>
                  <div className="text-2xl font-bold text-amber-400">{assignedInProcess.length}</div>
                  <p className="text-xs text-gray-500">Active loans</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-green-500" data-testid="processor-closed">
              <CardContent className="p-[24px]">
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-400">Closed</span>
                  <div className="text-2xl font-bold text-green-400">{assignedClosed.length}</div>
                  <p className="text-xs text-gray-500">{formatCurrency(closedVolume)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-red-500" data-testid="processor-withdrawn">
              <CardContent className="p-[24px]">
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-400">Withdrawn</span>
                  <div className="text-2xl font-bold text-red-400">{assignedWithdrawn.length}</div>
                  <p className="text-xs text-gray-500">{viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : 'This month'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* All-Time Processor Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[24px]">
          <Card className="bg-[#111] border-none rounded-xl">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-400">Total Active Loans</span>
                <div className="text-2xl font-bold text-white">{allTimeActive}</div>
                <p className="text-xs text-gray-500">Currently in process</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-none rounded-xl">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-400">All-Time Closed</span>
                <div className="text-2xl font-bold text-green-400">{allTimeClosed}</div>
                <p className="text-xs text-gray-500">Successfully closed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-none rounded-xl">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-400">All-Time Volume</span>
                <div className="text-2xl font-bold text-white">{formatCurrency(allTimeVolume)}</div>
                <p className="text-xs text-gray-500">Closed loan volume</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status Breakdown Chart + Turnaround Time + Aged Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px]">
          {/* Donut Chart */}
          <Card className="bg-[#111] border-none rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <PieChartIcon className="w-4 h-4 text-teal-500" />
                Pipeline Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-[24px] pt-0">
              {activeAssigned.length > 0 ? (() => {
                const statusData = [
                  { name: 'In Process', value: assignedInProcess.length, color: '#f59e0b' },
                  { name: 'Closed', value: assignedClosed.length, color: '#22c55e' },
                  { name: 'Withdrawn', value: assignedWithdrawn.length, color: '#ef4444' },
                ].filter(d => d.value > 0);
                return (
                  <div className="flex items-center gap-6">
                    <div className="w-[140px] h-[140px] flex-shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" strokeWidth={0}>
                            {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-col gap-3">
                      {statusData.map(d => (
                        <div key={d.name} className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                          <span className="text-sm text-gray-400">{d.name}</span>
                          <span className="text-sm font-bold text-white">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })() : (
                <div className="text-center py-6">
                  <PieChartIcon className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No loans this month</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Turnaround Time + Alerts */}
          <Card className="bg-[#111] border-none rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-500" />
                Processing Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-[24px] pt-0">
              {(() => {
                // Turnaround time for closed loans with submission dates
                const closedWithDates = myAssignedLoans.filter(l => l.status === 'Closed' && l.submissionDate && l.date);
                let avgTurnaround = 0;
                if (closedWithDates.length > 0) {
                  const totalDays = closedWithDates.reduce((sum, l) => {
                    const sub = parseLocalDate(l.submissionDate);
                    const close = parseLocalDate(l.date);
                    return sum + Math.max(0, Math.round((close - sub) / (1000 * 60 * 60 * 24)));
                  }, 0);
                  avgTurnaround = Math.round(totalDays / closedWithDates.length);
                }
                // Aged loans — in process 25+ days
                const today = new Date();
                const agedLoans = activeAssigned.filter(l => {
                  if (l.status !== 'In Process' && l.status !== 'Pending') return false;
                  const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
                  const days = Math.round((today - sub) / (1000 * 60 * 60 * 24));
                  return days >= 25;
                });

                return (
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-[24px]">
                      <div className="bg-[#0a0a0a] rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg Turnaround</p>
                        <p className="text-2xl font-bold text-teal-400">{closedWithDates.length > 0 ? `${avgTurnaround}d` : '—'}</p>
                        <p className="text-xs text-gray-500 mt-1">Submission to close</p>
                      </div>
                      <div
                        className={`bg-[#0a0a0a] rounded-lg p-4 ${agedLoans.length > 0 ? 'cursor-pointer hover:bg-[#151515] transition-colors' : ''}`}
                        onClick={() => {
                          if (agedLoans.length > 0) {
                            const enriched = agedLoans.map(l => {
                              const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
                              const days = Math.round((new Date() - sub) / (1000 * 60 * 60 * 24));
                              return { ...l, daysAged: days };
                            });
                            setAgedLoansData(enriched);
                            setShowAgedLoansDialog(true);
                          }
                        }}
                        data-testid="aged-loans-clickable"
                      >
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Aged Loans</p>
                        <p className={`text-2xl font-bold ${agedLoans.length > 0 ? 'text-red-400' : 'text-green-400'}`}>{agedLoans.length}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {agedLoans.length > 0 ? 'Click to view details' : '25+ days in process'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* My Assigned Loans Table */}
        <Card className="bg-[#111] border-none rounded-xl">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-500" />
              My Assigned Loans
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeAssigned.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="processor-loans-table">
                  <thead>
                    <tr className="border-b border-[#333]">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Client</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Loan Officer</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Lender</th>
                      <th className="text-right py-2 px-3 text-gray-400 font-medium">Amount</th>
                      <th className="text-center py-2 px-3 text-gray-400 font-medium">Status</th>
                      <th className="text-center py-2 px-3 text-gray-400 font-medium">Close Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAssigned.map((loan, idx) => (
                      <tr key={loan.id} className={`border-b border-[#222] ${idx % 2 === 0 ? 'bg-[#0d0d0d]' : ''} ${loan.status === 'Withdrawn' ? 'opacity-50' : ''}`}>
                        <td className="py-2.5 px-3 text-white font-medium">{loan.clientName}</td>
                        <td className="py-2.5 px-3 text-gray-300">{loan.userName}</td>
                        <td className="py-2.5 px-3 text-gray-300">{loan.lender || '—'}</td>
                        <td className="py-2.5 px-3 text-white text-right">{formatCurrency(loan.loanAmount)}</td>
                        <td className="py-2.5 px-3 text-center">
                          <button onClick={() => handleDashboardStatusClick(loan)} className="cursor-pointer" data-testid={`dash-status-btn-${loan.id}`}>
                            <Badge className={
                              loan.status === 'Closed' ? 'bg-green-900/50 text-green-400 border border-green-800 hover:brightness-125 transition-all' :
                              loan.status === 'Withdrawn' ? 'bg-red-900/50 text-red-400 border border-red-800 hover:brightness-125 transition-all' :
                              'bg-amber-900/50 text-amber-400 border border-amber-800 hover:brightness-125 transition-all'
                            }>
                              {loan.status === 'Pending' ? 'In Process' : loan.status}
                            </Badge>
                          </button>
                        </td>
                        <td className="py-2.5 px-3 text-center text-gray-300">{formatDate(loan.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#444] bg-[#1a1a1a]">
                      <td className="py-2.5 px-3 text-white font-bold" colSpan="3">Total ({activeAssigned.length} loans)</td>
                      <td className="py-2.5 px-3 text-white text-right font-bold">{formatCurrency(assignedVolume)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500">No loans assigned to you {viewMode === 'ytd' ? 'this year' : 'this month'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company-Wide Overview (No Commission) */}
        <Card className="bg-[#111] border-none rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              {viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : formatMonth(selectedMonth)} Company Closings
            </CardTitle>
            <CardDescription className="text-gray-500">
              {companyMonthClosed.length} closed loans — {formatCurrency(companyMonthVolume)} total volume
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#333]">
                    <th className="text-left py-2 px-3 text-gray-400 font-semibold w-[50%]">Broker</th>
                    <th className="text-right py-2 px-3 text-gray-400 font-semibold w-[35%]">Volume</th>
                    <th className="text-center py-2 px-3 text-gray-400 font-semibold w-[15%]">Units</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry) => {
                    const maxVolume = leaderboard[0]?.total_volume || 1;
                    let rowColor = entry.total_volume === 0
                      ? 'bg-red-950/40 border-l-4 border-l-red-500'
                      : entry.total_volume >= maxVolume * 0.5
                      ? 'bg-green-950/40 border-l-4 border-l-green-500'
                      : 'bg-yellow-950/30 border-l-4 border-l-yellow-500';
                    return (
                      <tr key={entry.user_id} className={`${rowColor} border-b border-[#222]`}>
                        <td className="py-2.5 px-3 text-white font-medium">{entry.user_name}</td>
                        <td className="py-2.5 px-3 text-right font-semibold">
                          <span className={entry.total_volume === 0 ? 'text-red-400' : entry.total_volume >= maxVolume * 0.5 ? 'text-green-400' : 'text-yellow-400'}>
                            {formatCurrency(entry.total_volume)}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className={entry.loan_count === 0 ? 'text-red-400' : entry.loan_count >= 2 ? 'text-green-400' : 'text-yellow-400'}>
                            {entry.loan_count}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#444] bg-[#1a1a1a]">
                    <td className="py-2.5 px-3 text-white font-bold">Total:</td>
                    <td className="py-2.5 px-3 text-right text-green-400 font-bold">
                      {formatCurrency(leaderboard.reduce((sum, e) => sum + e.total_volume, 0))}
                    </td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">
                      {leaderboard.reduce((sum, e) => sum + e.loan_count, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500">No company closings {viewMode === 'ytd' ? 'this year' : 'this month'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Aged Loans Dialog */}
        <Dialog open={showAgedLoansDialog} onOpenChange={setShowAgedLoansDialog}>
          <DialogContent className="bg-[#111] border-[#333] text-white max-w-3xl w-[90vw]">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Aged Loans — 25+ Days in Process
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                {agedLoansData.length} loan{agedLoansData.length !== 1 ? 's' : ''} have been in process for over 25 days
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
              {agedLoansData.map(loan => (
                <div key={loan.id} className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-semibold">{loan.clientName}</span>
                    <Badge className="bg-red-900/50 text-red-400 border border-red-800">{loan.daysAged}d aged</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">LO:</span> <span className="text-gray-300">{loan.userName}</span></div>
                    <div><span className="text-gray-500">Lender:</span> <span className="text-gray-300">{loan.lender || '—'}</span></div>
                    <div><span className="text-gray-500">Amount:</span> <span className="text-gray-300">{formatCurrency(loan.loanAmount)}</span></div>
                    <div><span className="text-gray-500">Loan #:</span> <span className="text-gray-300">{loan.loanNumber}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Status Picker Dialog (Processor) */}
        <Dialog open={statusPickerDialog.open} onOpenChange={(open) => !open && setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' })}>
          <DialogContent className="bg-[#111] border-[#333] text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white">Update Loan Status</DialogTitle>
              <DialogDescription className="text-gray-400">Select a new status for this loan</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-2">
              {['In Process', 'Closed', 'Withdrawn'].map(status => {
                const isActive = statusPickerDialog.currentStatus === status || 
                  (statusPickerDialog.currentStatus === 'Pending' && status === 'In Process');
                return (
                  <Button
                    key={status}
                    variant="outline"
                    data-testid={`dash-status-option-${status.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => handleDashboardStatusSelect(status)}
                    className={`justify-start h-12 text-sm font-medium transition-all ${
                      status === 'In Process'
                        ? isActive 
                          ? 'bg-amber-900/50 text-amber-400 border-amber-600 ring-2 ring-amber-500/30'
                          : 'bg-amber-900/20 text-amber-400 border-amber-800 hover:bg-amber-900/40'
                      : status === 'Closed'
                        ? isActive
                          ? 'bg-green-900/50 text-green-400 border-green-600 ring-2 ring-green-500/30'
                          : 'bg-green-900/20 text-green-400 border-green-800 hover:bg-green-900/40'
                      : isActive
                          ? 'bg-red-900/50 text-red-400 border-red-600 ring-2 ring-red-500/30'
                          : 'bg-red-900/20 text-red-400 border-red-800 hover:bg-red-900/40'
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full mr-3 ${
                      status === 'In Process' ? 'bg-amber-400' : status === 'Closed' ? 'bg-green-400' : 'bg-red-400'
                    }`} />
                    {status}
                    {isActive && <span className="ml-auto text-xs opacity-60">Current</span>}
                  </Button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Confirm Closing & Funding Dialog (Processor) */}
        <Dialog open={fundingDateDialog.open} onOpenChange={(open) => {
          if (!open) setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' });
        }}>
          <DialogContent className="bg-[#111] border-[#222] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Confirm Closing & Funding</DialogTitle>
              <DialogDescription className="text-gray-400">
                Confirm the closing date and enter the funding date to mark this loan as closed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="dashClosingDate" className="text-gray-300">Closing Date</Label>
                <Input
                  id="dashClosingDate"
                  type="date"
                  data-testid="dash-closing-date-input"
                  value={fundingDateDialog.closingDate}
                  onChange={(e) => setFundingDateDialog({ ...fundingDateDialog, closingDate: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dashFundingDate" className="text-gray-300">Funding Date</Label>
                <Input
                  id="dashFundingDate"
                  type="date"
                  data-testid="dash-funding-date-input"
                  value={fundingDateDialog.fundingDate}
                  onChange={(e) => setFundingDateDialog({ ...fundingDateDialog, fundingDate: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={handleDashboardFundingSubmit}
                data-testid="dash-mark-as-closed-btn"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                Mark as Closed
              </Button>
              <Button
                variant="outline"
                onClick={() => setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' })}
                className="border-[#333] text-gray-300 hover:bg-[#1a1a1a]"
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-6" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Admin Loan Officer Selector */}
      {isAdmin && (
        <Card className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-800/50 rounded-xl">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Viewing:</span>
                <button
                  onClick={() => setShowOfficerDropdown(true)}
                  data-testid="officer-dropdown-trigger"
                  className="bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white hover:border-blue-500 focus:border-blue-500 focus:outline-none min-w-[220px] flex items-center justify-between transition-colors"
                >
                  <span>
                    {selectedOfficer === 'mine' ? 'My Loans Only' : 
                     selectedOfficer === 'all' ? 'Company Wide (Sales)' :
                     selectedOfficer === 'all_processing' ? 'Company Wide (Processing)' :
                     loanOfficers.find(o => o.id === selectedOfficer)?.name || 'Select'}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              {selectedOfficer === 'all' && (
                <Badge className="bg-purple-500 text-white">Company Wide (Sales)</Badge>
              )}
              {selectedOfficer === 'all_processing' && (
                <Badge className="bg-teal-500 text-white">Company Wide (Processing)</Badge>
              )}
              {selectedOfficer !== 'mine' && selectedOfficer !== 'all' && selectedOfficer !== 'all_processing' && viewingProcessor && (
                <Badge className="bg-teal-500 text-white">
                  Viewing Processor: {viewingProcessorName}
                </Badge>
              )}
              {selectedOfficer !== 'mine' && selectedOfficer !== 'all' && selectedOfficer !== 'all_processing' && !viewingProcessor && (
                <Badge className="bg-purple-500 text-white">
                  Viewing: {loanOfficers.find(o => o.id === selectedOfficer)?.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Officer Selection Dialog */}
      <Dialog open={showOfficerDropdown} onOpenChange={setShowOfficerDropdown}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Team Member</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose a loan officer or processor to view their data</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1 max-h-[70vh] overflow-y-auto">
            {/* Quick options */}
            <div className="grid grid-cols-3 gap-2 mb-1">
              <button
                onClick={() => { setSelectedOfficer('mine'); setShowOfficerDropdown(false); }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                  selectedOfficer === 'mine' ? 'bg-blue-900/40 text-blue-400 border border-blue-700 ring-2 ring-blue-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-blue-600 hover:bg-[#1e1e1e]'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                  <Users className="w-3.5 h-3.5 text-blue-400" />
                </div>
                My Loans Only
              </button>
              <button
                onClick={() => { setSelectedOfficer('all'); setShowOfficerDropdown(false); }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                  selectedOfficer === 'all' ? 'bg-purple-900/40 text-purple-400 border border-purple-700 ring-2 ring-purple-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-purple-600 hover:bg-[#1e1e1e]'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-purple-400" />
                </div>
                Company Wide (Sales)
              </button>
              <button
                onClick={() => { setSelectedOfficer('all_processing'); setShowOfficerDropdown(false); }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                  selectedOfficer === 'all_processing' ? 'bg-teal-900/40 text-teal-400 border border-teal-700 ring-2 ring-teal-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-teal-600 hover:bg-[#1e1e1e]'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-teal-900/50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-teal-400" />
                </div>
                Company Wide (Processing)
              </button>
            </div>

            {/* Loan Officers Grid */}
            {officersList.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs text-green-400 uppercase tracking-widest font-bold">Loan Officers</span>
                  <div className="flex-1 h-px bg-green-900/50 ml-1" />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {officersList.map(officer => (
                    <button
                      key={officer.id}
                      onClick={() => { setSelectedOfficer(officer.id); setShowOfficerDropdown(false); }}
                      className={`px-2 py-2 rounded-md text-xs transition-all flex items-center gap-2 ${
                        selectedOfficer === officer.id ? 'bg-green-900/30 text-green-400 border border-green-800 ring-1 ring-green-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-green-700 hover:bg-[#1e1e1e]'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-green-900/40 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-green-400">
                        {officer.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="truncate font-medium">{officer.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Processors Grid */}
            {processorsList.length > 0 && (
              <div>
                {officersList.length > 0 && <div className="h-px bg-[#333] my-1" />}
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-teal-500" />
                  <span className="text-xs text-teal-400 uppercase tracking-widest font-bold">Processors</span>
                  <div className="flex-1 h-px bg-teal-900/50 ml-1" />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {processorsList.map(processor => (
                    <button
                      key={processor.id}
                      onClick={() => { setSelectedOfficer(processor.id); setShowOfficerDropdown(false); }}
                      className={`px-2 py-2 rounded-md text-xs transition-all flex items-center gap-2 ${
                        selectedOfficer === processor.id ? 'bg-teal-900/30 text-teal-400 border border-teal-800 ring-1 ring-teal-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-teal-700 hover:bg-[#1e1e1e]'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-teal-900/40 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-teal-400">
                        {processor.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="truncate font-medium">{processor.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* COMPANY WIDE (PROCESSING) VIEW */}
      {isAdmin && selectedOfficer === 'all_processing' ? (
        <>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-2xl lg:text-3xl font-semibold text-white">
                Processing Command Center
              </h1>
              <p className="text-gray-400 text-sm">Company-wide processor workload, pipeline health & aged loan alerts</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-[#1a1a1a] rounded-lg border border-[#333] p-0.5">
                <button onClick={() => setProcessingViewMode('monthly')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${processingViewMode === 'monthly' ? 'bg-teal-600 text-white' : 'text-gray-400 hover:text-white'}`}>Monthly</button>
                <button onClick={() => setProcessingViewMode('ytd')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${processingViewMode === 'ytd' ? 'bg-teal-600 text-white' : 'text-gray-400 hover:text-white'}`}>YTD</button>
              </div>
              <Badge className="bg-teal-900/50 text-teal-400 border border-teal-700 text-sm px-3 py-1.5">
                Company Wide (Processing)
              </Badge>
            </div>
          </div>

          {(() => {
            const today = new Date();
            const currentYear = today.getFullYear();
            const isProcYTD = processingViewMode === 'ytd';
            const procPeriodLabel = isProcYTD ? `YTD ${currentYear}` : formatMonth(selectedMonth);

            // Filter loans by period
            const allPeriodLoans = isProcYTD
              ? loans.filter(l => { const d = parseLocalDate(l.date); return d.getFullYear() === currentYear; })
              : loans.filter(l => { const d = parseLocalDate(l.date); return d.getMonth() === selectedMonth.getMonth() && d.getFullYear() === selectedMonth.getFullYear(); });

            // All active (non-closed, non-withdrawn) loans from period
            const activeLoans = allPeriodLoans.filter(l => l.status !== 'Withdrawn' && l.status !== 'Closed');
            const closedLoans = allPeriodLoans.filter(l => l.status === 'Closed');
            const withdrawnLoans = allPeriodLoans.filter(l => l.status === 'Withdrawn');
            const totalActive = activeLoans.length;

            // Aged loans — in process 25+ days
            const agedThreshold = 25;
            const allAgedLoans = activeLoans.filter(l => {
              if (l.status !== 'In Process' && l.status !== 'Pending') return false;
              const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
              return Math.round((today - sub) / (1000 * 60 * 60 * 24)) >= agedThreshold;
            }).map(l => {
              const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
              return { ...l, daysAged: Math.round((today - sub) / (1000 * 60 * 60 * 24)) };
            }).sort((a, b) => b.daysAged - a.daysAged);

            // Critical (45+ days) vs Warning (25-44 days)
            const criticalLoans = allAgedLoans.filter(l => l.daysAged >= 45);
            const warningLoans = allAgedLoans.filter(l => l.daysAged >= 25 && l.daysAged < 45);

            // Avg turnaround for closed loans
            const closedWithDates = closedLoans.filter(l => l.submissionDate && l.date);
            let avgTurnaround = 0;
            if (closedWithDates.length > 0) {
              const totalDays = closedWithDates.reduce((sum, l) => {
                const sub = parseLocalDate(l.submissionDate);
                const close = parseLocalDate(l.date);
                return sum + Math.max(0, Math.round((close - sub) / (1000 * 60 * 60 * 24)));
              }, 0);
              avgTurnaround = Math.round(totalDays / closedWithDates.length);
            }

            // Avg days in pipeline for active loans
            let avgDaysActive = 0;
            if (activeLoans.length > 0) {
              const totalDaysActive = activeLoans.reduce((sum, l) => {
                const created = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
                return sum + Math.max(0, Math.round((today - created) / (1000 * 60 * 60 * 24)));
              }, 0);
              avgDaysActive = Math.round(totalDaysActive / activeLoans.length);
            }

            // Group by processor
            const processorMap = {};
            processorsList.forEach(p => { processorMap[p.name] = { count: 0, volume: 0, aged: 0, closed: 0, closedVolume: 0, avgDays: 0, loans: [] }; });

            // Count active loans per processor
            activeLoans.forEach(l => {
              const procName = l.processor || 'Self Processing';
              if (!processorMap[procName]) processorMap[procName] = { count: 0, volume: 0, aged: 0, closed: 0, closedVolume: 0, avgDays: 0, loans: [] };
              processorMap[procName].count += 1;
              processorMap[procName].volume += (l.loanAmount || 0);
              processorMap[procName].loans.push(l);
              // Check if aged
              const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
              const days = Math.round((today - sub) / (1000 * 60 * 60 * 24));
              if (days >= agedThreshold && (l.status === 'In Process' || l.status === 'Pending')) {
                processorMap[procName].aged += 1;
              }
            });

            // Count closed loans per processor
            closedLoans.forEach(l => {
              const procName = l.processor || 'Self Processing';
              if (!processorMap[procName]) processorMap[procName] = { count: 0, volume: 0, aged: 0, closed: 0, closedVolume: 0, avgDays: 0, loans: [] };
              processorMap[procName].closed += 1;
              processorMap[procName].closedVolume += (l.loanAmount || 0);
            });

            // Calculate avg days for each processor's active loans
            Object.entries(processorMap).forEach(([name, data]) => {
              if (data.loans.length > 0) {
                const totalD = data.loans.reduce((sum, l) => {
                  const created = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
                  return sum + Math.max(0, Math.round((today - created) / (1000 * 60 * 60 * 24)));
                }, 0);
                data.avgDays = Math.round(totalD / data.loans.length);
              }
            });

            const sortedProcessors = Object.entries(processorMap).sort((a, b) => b[1].count - a[1].count);

            // Chart data for processor workload bar chart
            const barChartData = sortedProcessors
              .filter(([name, d]) => processorsList.find(p => p.name === name) || d.count > 0)
              .map(([name, d]) => ({
                name: name.split(' ')[0],
                fullName: name,
                active: d.count,
                aged: d.aged,
                closed: d.closed,
              }));

            // Status distribution pie
            const statusCounts = {};
            activeLoans.forEach(l => {
              const st = l.status === 'Pending' ? 'In Process' : l.status;
              statusCounts[st] = (statusCounts[st] || 0) + 1;
            });
            const statusColors = { 'In Process': '#f59e0b', 'Submitted': '#3b82f6', 'Pending': '#f59e0b' };
            const statusData = Object.entries(statusCounts).map(([name, value]) => ({
              name, value, color: statusColors[name] || '#6b7280'
            }));

            return (
              <div className="space-y-6">
                {/* Month selector for Monthly mode */}
                {!isProcYTD && (
                  <div className="flex items-center justify-center gap-4">
                    <button onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))} className="p-1.5 rounded-lg bg-[#1a1a1a] border border-[#333] text-gray-400 hover:text-white hover:border-teal-600 transition-all">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="text-lg font-bold text-white">{formatMonth(selectedMonth)}</div>
                    <button onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))} className="p-1.5 rounded-lg bg-[#1a1a1a] border border-[#333] text-gray-400 hover:text-white hover:border-teal-600 transition-all">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* KPI Strip */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-teal-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Active Pipeline</p>
                      <p className="text-2xl font-bold text-white mt-1">{totalActive}</p>
                      <p className="text-xs text-gray-500 mt-0.5">${activeLoans.reduce((s, l) => s + (l.loanAmount || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-green-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">{procPeriodLabel} Closed</p>
                      <p className="text-2xl font-bold text-green-400 mt-1">{closedLoans.length}</p>
                      <p className="text-xs text-gray-500 mt-0.5">${closedLoans.reduce((s, l) => s + (l.loanAmount || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Avg Turnaround</p>
                      <p className="text-2xl font-bold text-blue-400 mt-1">{closedWithDates.length > 0 ? `${avgTurnaround}d` : '—'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Submit to close</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Avg Days Active</p>
                      <p className="text-2xl font-bold text-amber-400 mt-1">{activeLoans.length > 0 ? `${avgDaysActive}d` : '—'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">In pipeline</p>
                    </CardContent>
                  </Card>
                  <Card className={`border-none rounded-xl relative overflow-hidden ${allAgedLoans.length > 0 ? 'bg-red-950/40' : 'bg-[#111]'}`}>
                    <div className={`absolute top-0 left-0 w-1 h-full ${allAgedLoans.length > 0 ? 'bg-red-500' : 'bg-gray-600'}`} />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Aged Loans</p>
                      <p className={`text-2xl font-bold mt-1 ${allAgedLoans.length > 0 ? 'text-red-400' : 'text-green-400'}`}>{allAgedLoans.length}</p>
                      <p className="text-xs text-gray-500 mt-0.5">25+ days in process</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Two-column: Processor Workload Table + Pipeline Donut */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Processor Workload Table — takes 2/3 */}
                  <Card className="bg-[#111] border-none rounded-xl lg:col-span-2">
                    <CardContent className="p-0">
                      <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
                        <div>
                          <h2 className="text-base font-semibold text-white flex items-center gap-2">
                            <Users className="w-4 h-4 text-teal-500" />
                            Processor Workload
                          </h2>
                          <p className="text-[11px] text-gray-500 mt-0.5">Active loans, pipeline volume, aged alerts & performance</p>
                        </div>
                        <Badge className="bg-[#1a1a1a] text-gray-400 border-[#333] text-xs">{processorsList.length} Processors</Badge>
                      </div>
                      <table className="w-full" data-testid="processing-overview-table">
                        <thead>
                          <tr className="border-b border-[#222]">
                            <th className="text-left py-2.5 px-5 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Processor</th>
                            <th className="text-center py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Active</th>
                            <th className="text-center py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Aged</th>
                            <th className="text-center py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Closed</th>
                            <th className="text-center py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Avg Days</th>
                            <th className="text-right py-2.5 px-5 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Pipeline $</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedProcessors.map(([name, data], idx) => {
                            const isKnownProcessor = processorsList.find(p => p.name === name);
                            const maxCount = Math.max(...sortedProcessors.map(([, d]) => d.count), 1);
                            const barWidth = (data.count / maxCount) * 100;
                            return (
                              <tr
                                key={name}
                                className={`border-b border-[#1a1a1a] transition-colors ${isKnownProcessor ? 'hover:bg-teal-900/10 cursor-pointer' : 'hover:bg-[#1a1a1a]/60'}`}
                                onClick={() => { if (isKnownProcessor) setSelectedOfficer(isKnownProcessor.id); }}
                                data-testid={`processor-row-${name.replace(/\s/g, '-').toLowerCase()}`}
                              >
                                <td className="py-3 px-5">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${
                                      isKnownProcessor ? 'bg-teal-900/40 text-teal-400' : 'bg-gray-800 text-gray-500'
                                    }`}>
                                      {name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <div className="min-w-0">
                                      <span className={`text-sm font-medium block truncate ${isKnownProcessor ? 'text-white group-hover:text-teal-400' : 'text-white'}`}>{name}</span>
                                      {/* Mini workload bar */}
                                      <div className="w-24 h-1 bg-[#222] rounded-full mt-1">
                                        <div className="h-full bg-teal-500/60 rounded-full transition-all" style={{ width: `${barWidth}%` }} />
                                      </div>
                                    </div>
                                    {isKnownProcessor && <ChevronRight className="w-3.5 h-3.5 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <span className={`text-base font-bold ${data.count > 0 ? 'text-teal-400' : 'text-gray-600'}`}>{data.count}</span>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  {data.aged > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-sm font-bold text-red-400">
                                      <AlertTriangle className="w-3 h-3" />
                                      {data.aged}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-green-500">0</span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <span className="text-sm text-green-400 font-medium">{data.closed}</span>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <span className={`text-sm font-medium ${data.avgDays >= 30 ? 'text-red-400' : data.avgDays >= 20 ? 'text-amber-400' : 'text-gray-300'}`}>
                                    {data.count > 0 ? `${data.avgDays}d` : '—'}
                                  </span>
                                </td>
                                <td className="py-3 px-5 text-right">
                                  <span className="text-sm text-gray-300">${data.volume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {sortedProcessors.length > 0 && (
                          <tfoot>
                            <tr className="border-t-2 border-[#333] bg-[#0a0a0a]">
                              <td className="py-3 px-5 text-white font-semibold text-sm">Total</td>
                              <td className="py-3 px-4 text-center text-teal-400 font-bold">{totalActive}</td>
                              <td className="py-3 px-4 text-center text-red-400 font-bold">{allAgedLoans.length}</td>
                              <td className="py-3 px-4 text-center text-green-400 font-bold">{closedLoans.length}</td>
                              <td className="py-3 px-4 text-center text-gray-400">{activeLoans.length > 0 ? `${avgDaysActive}d` : '—'}</td>
                              <td className="py-3 px-5 text-right text-white font-semibold">${activeLoans.reduce((s, l) => s + (l.loanAmount || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </CardContent>
                  </Card>

                  {/* Pipeline Status Donut — takes 1/3 */}
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
                        <PieChartIcon className="w-4 h-4 text-teal-500" />
                        Status Breakdown
                      </h3>
                      {statusData.length > 0 ? (
                        <div className="flex flex-col items-center">
                          <div className="w-[160px] h-[160px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} dataKey="value" strokeWidth={0}>
                                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                </Pie>
                                <Tooltip
                                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                                  formatter={(value, name) => [`${value} loans`, name]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="flex flex-col gap-2 mt-4 w-full">
                            {statusData.map(d => (
                              <div key={d.name} className="flex items-center justify-between px-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                  <span className="text-xs text-gray-400">{d.name}</span>
                                </div>
                                <span className="text-xs font-bold text-white">{d.value}</span>
                              </div>
                            ))}
                          </div>
                          {/* Quick stats under donut */}
                          <div className="mt-4 pt-4 border-t border-[#222] w-full grid grid-cols-2 gap-3">
                            <div className="bg-[#0a0a0a] rounded-lg p-3 text-center">
                              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Withdrawn</p>
                              <p className="text-lg font-bold text-red-400 mt-0.5">{withdrawnLoans.length}</p>
                            </div>
                            <div className="bg-[#0a0a0a] rounded-lg p-3 text-center">
                              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Pull Through %</p>
                              <p className="text-lg font-bold text-green-400 mt-0.5">
                                {loans.length > 0 ? `${Math.round((closedLoans.length / loans.length) * 100)}%` : '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-10">
                          <PieChartIcon className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">No active loans</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Aged Loans Alert Section */}
                <Card className={`border-none rounded-xl ${allAgedLoans.length > 0 ? 'bg-gradient-to-r from-red-950/30 to-[#111]' : 'bg-[#111]'}`}>
                  <CardContent className="p-0">
                    <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${allAgedLoans.length > 0 ? 'bg-red-900/50' : 'bg-green-900/50'}`}>
                          <AlertTriangle className={`w-4 h-4 ${allAgedLoans.length > 0 ? 'text-red-400' : 'text-green-400'}`} />
                        </div>
                        <div>
                          <h2 className="text-base font-semibold text-white">Aged Loans</h2>
                          <p className="text-[11px] text-gray-500">Loans in process for 25+ days require attention</p>
                        </div>
                      </div>
                      {allAgedLoans.length > 0 && (
                        <div className="flex items-center gap-3">
                          {criticalLoans.length > 0 && (
                            <Badge className="bg-red-900/60 text-red-300 border border-red-800 text-xs">
                              {criticalLoans.length} Critical (45d+)
                            </Badge>
                          )}
                          {warningLoans.length > 0 && (
                            <Badge className="bg-amber-900/60 text-amber-300 border border-amber-800 text-xs">
                              {warningLoans.length} Warning (25d+)
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    {allAgedLoans.length > 0 ? (
                      <table className="w-full" data-testid="aged-loans-table">
                        <thead>
                          <tr className="border-b border-[#222]">
                            <th className="text-left py-2.5 px-5 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Client</th>
                            <th className="text-left py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Loan Officer</th>
                            <th className="text-left py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Processor</th>
                            <th className="text-left py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Lender</th>
                            <th className="text-right py-2.5 px-4 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Amount</th>
                            <th className="text-center py-2.5 px-5 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Days Aged</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allAgedLoans.map((loan, idx) => (
                            <tr key={loan.id} className={`border-b border-[#1a1a1a] ${loan.daysAged >= 45 ? 'bg-red-950/20' : ''}`}>
                              <td className="py-2.5 px-5 text-white text-sm font-medium">{loan.clientName}</td>
                              <td className="py-2.5 px-4 text-gray-300 text-sm">{loan.userName || '—'}</td>
                              <td className="py-2.5 px-4 text-teal-400 text-sm">{loan.processor || 'Self Processing'}</td>
                              <td className="py-2.5 px-4 text-gray-300 text-sm">{loan.lender || '—'}</td>
                              <td className="py-2.5 px-4 text-right text-white text-sm">${(loan.loanAmount || 0).toLocaleString()}</td>
                              <td className="py-2.5 px-5 text-center">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                  loan.daysAged >= 45 ? 'bg-red-900/60 text-red-300' : 'bg-amber-900/60 text-amber-300'
                                }`}>
                                  {loan.daysAged}d
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-10 text-center">
                        <div className="w-12 h-12 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-3">
                          <Zap className="w-5 h-5 text-green-400" />
                        </div>
                        <p className="text-green-400 font-medium text-sm">All Clear</p>
                        <p className="text-gray-500 text-xs mt-1">No loans have exceeded 25 days in process</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Processor Workload Bar Chart */}
                {(() => {
                  const filteredProcessors = sortedProcessors.filter(([name, d]) => processorsList.find(p => p.name === name) && (d.count > 0 || d.aged > 0));
                  if (filteredProcessors.length === 0) return null;
                  const chartData = filteredProcessors.map(([name, d]) => ({
                    name: name.length > 14 ? name.split(' ')[0] : name,
                    Active: d.count,
                    Aged: d.aged,
                  }));
                  const chartWidth = Math.min(100, Math.max(40, filteredProcessors.length * 25));
                  return (
                    <Card className="bg-[#111] border-none rounded-xl">
                      <CardContent className="p-5 pb-3">
                        <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                          <BarChart2 className="w-4 h-4 text-teal-500" />
                          Workload Distribution
                          <div className="flex items-center gap-4 ml-auto">
                            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-teal-400" /><span className="text-[11px] text-gray-500">Active</span></div>
                            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-red-500" /><span className="text-[11px] text-gray-500">Aged (25d+)</span></div>
                          </div>
                        </h3>
                        <div className="h-[180px]" style={{ width: chartWidth + '%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={chartData}
                              barGap={2}
                              barCategoryGap="35%"
                              margin={{ top: 5, right: 5, left: -15, bottom: 0 }}
                            >
                              <XAxis dataKey="name" tick={{ fill: '#d1d5db', fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false} dy={4} />
                              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                              <Tooltip
                                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                              />
                              <Bar dataKey="Active" fill="#2dd4bf" radius={[6, 6, 0, 0]} maxBarSize={32} />
                              <Bar dataKey="Aged" fill="#ef4444" radius={[6, 6, 0, 0]} maxBarSize={32} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            );
          })()}
        </>
      ) :
      isAdmin && selectedOfficer === 'all' ? (
        <>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-2xl lg:text-3xl font-semibold text-white">
                Sales Command Center
              </h1>
              <p className="text-gray-400 text-sm">Company-wide loan officer performance, pipeline health & production metrics</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-[#1a1a1a] rounded-lg border border-[#333] p-0.5">
                <button onClick={() => setSalesViewMode('monthly')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${salesViewMode === 'monthly' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}>Monthly</button>
                <button onClick={() => setSalesViewMode('ytd')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${salesViewMode === 'ytd' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}>YTD</button>
              </div>
              <Badge className="bg-purple-900/50 text-purple-400 border border-purple-700 text-sm px-3 py-1.5">
                Company Wide (Sales)
              </Badge>
            </div>
          </div>

          {(() => {
            const today = new Date();
            const currentMonth = today.getMonth();
            const currentYear = today.getFullYear();
            const isYTD = salesViewMode === 'ytd';
            const periodLabel = isYTD ? `YTD ${currentYear}` : formatMonth(selectedMonth);

            // All loans (unfiltered for pull-through calculation)
            const allLoansRaw = loans;

            // Period-filtered loans
            const periodLoans = isYTD
              ? allLoansRaw.filter(l => { const d = parseLocalDate(l.date); return d.getFullYear() === currentYear; })
              : allLoansRaw.filter(l => { const d = parseLocalDate(l.date); return d.getMonth() === selectedMonth.getMonth() && d.getFullYear() === selectedMonth.getFullYear(); });

            const periodActive = periodLoans.filter(l => l.status !== 'Withdrawn' && l.status !== 'Closed');
            const periodClosed = periodLoans.filter(l => l.status === 'Closed');
            const periodWithdrawn = periodLoans.filter(l => l.status === 'Withdrawn');
            const periodClosedVolume = periodClosed.reduce((s, l) => s + (l.loanAmount || 0), 0);
            const periodActiveVolume = periodActive.reduce((s, l) => s + (l.loanAmount || 0), 0);
            const avgLoanSize = periodClosed.length > 0 ? periodClosedVolume / periodClosed.length : 0;
            const pullThrough = periodLoans.length > 0 ? Math.round((periodClosed.length / periodLoans.length) * 100) : 0;

            // Group by Loan Officer using period loans
            const loMap = {};
            officersList.forEach(o => {
              loMap[o.name] = { id: o.id, brokerType: o.broker_type, active: 0, closed: 0, withdrawn: 0, closedVolume: 0, activeVolume: 0, loans: [] };
            });

            periodLoans.forEach(l => {
              const loName = l.userName || 'Unknown';
              if (!loMap[loName]) {
                loMap[loName] = { id: null, brokerType: null, active: 0, closed: 0, withdrawn: 0, closedVolume: 0, activeVolume: 0, loans: [] };
              }
              loMap[loName].loans.push(l);
              if (l.status === 'Closed') {
                loMap[loName].closed += 1;
                loMap[loName].closedVolume += (l.loanAmount || 0);
              } else if (l.status === 'Withdrawn') {
                loMap[loName].withdrawn += 1;
              } else {
                loMap[loName].active += 1;
                loMap[loName].activeVolume += (l.loanAmount || 0);
              }
            });

            const sortedLOs = Object.entries(loMap).sort((a, b) => {
              if (b[1].closed !== a[1].closed) return b[1].closed - a[1].closed;
              return b[1].closedVolume - a[1].closedVolume;
            });

            // Lead source breakdown from period loans
            const leadSourceMap = {};
            periodLoans.forEach(l => {
              const src = l.leadSource || 'Unknown';
              if (!leadSourceMap[src]) leadSourceMap[src] = { total: 0, closed: 0, volume: 0 };
              leadSourceMap[src].total += 1;
              if (l.status === 'Closed') {
                leadSourceMap[src].closed += 1;
                leadSourceMap[src].volume += (l.loanAmount || 0);
              }
            });
            const leadSourceData = Object.entries(leadSourceMap).sort((a, b) => b[1].total - a[1].total);
            const leadSourceColors = ['#a855f7', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

            // Lender breakdown from period loans
            const lenderMap = {};
            periodLoans.forEach(l => {
              const lender = l.lender || 'Unknown';
              if (!lenderMap[lender]) lenderMap[lender] = { total: 0, closed: 0, volume: 0 };
              lenderMap[lender].total += 1;
              if (l.status === 'Closed') {
                lenderMap[lender].closed += 1;
                lenderMap[lender].volume += (l.loanAmount || 0);
              }
            });
            const sortedLenders = Object.entries(lenderMap).sort((a, b) => b[1].total - a[1].total);

            // Status distribution from period loans
            const statusMap = {};
            periodLoans.forEach(l => {
              const st = l.status === 'Pending' ? 'In Process' : l.status;
              statusMap[st] = (statusMap[st] || 0) + 1;
            });
            const statusColors = { 'In Process': '#f59e0b', 'Closed': '#22c55e', 'Withdrawn': '#ef4444', 'Submitted': '#3b82f6' };
            const statusData = Object.entries(statusMap).map(([name, value]) => ({
              name, value, color: statusColors[name] || '#6b7280'
            }));

            // State breakdown from period loans — only states that have actual values
            const stateMap = {};
            periodLoans.forEach(l => {
              const st = l.state;
              if (!st || st === 'Unknown' || st.trim() === '') return;
              if (!stateMap[st]) stateMap[st] = { total: 0, closed: 0, active: 0, volume: 0, closedVolume: 0 };
              stateMap[st].total += 1;
              if (l.status === 'Closed') {
                stateMap[st].closed += 1;
                stateMap[st].closedVolume += (l.loanAmount || 0);
              } else if (l.status !== 'Withdrawn') {
                stateMap[st].active += 1;
              }
              stateMap[st].volume += (l.loanAmount || 0);
            });
            const sortedStates = Object.entries(stateMap).sort((a, b) => b[1].total - a[1].total);
            const stateColors = ['#a855f7', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#10b981', '#f97316'];

            return (
              <div className="space-y-6">
                {/* Month selector for Monthly mode */}
                {!isYTD && (
                  <div className="flex items-center justify-center gap-4">
                    <button onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))} className="p-1.5 rounded-lg bg-[#1a1a1a] border border-[#333] text-gray-400 hover:text-white hover:border-purple-600 transition-all">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="text-lg font-bold text-white">{formatMonth(selectedMonth)}</div>
                    <button onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))} className="p-1.5 rounded-lg bg-[#1a1a1a] border border-[#333] text-gray-400 hover:text-white hover:border-purple-600 transition-all">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* KPI Strip */}
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-purple-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Active Pipeline</p>
                      <p className="text-2xl font-bold text-white mt-1">{periodActive.length}</p>
                      <p className="text-xs text-gray-500 mt-0.5">${periodActiveVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-green-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">{isYTD ? 'YTD' : 'Period'} Closed</p>
                      <p className="text-2xl font-bold text-green-400 mt-1">{periodClosed.length}</p>
                      <p className="text-xs text-gray-500 mt-0.5">${periodClosedVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Total Loans</p>
                      <p className="text-2xl font-bold text-blue-400 mt-1">{periodLoans.length}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{periodLabel}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Closed Volume</p>
                      <p className="text-2xl font-bold text-amber-400 mt-1">${periodClosedVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{periodClosed.length} loans</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-teal-500" />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Avg Loan Size</p>
                      <p className="text-2xl font-bold text-teal-400 mt-1">${avgLoanSize.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Closed loans</p>
                    </CardContent>
                  </Card>
                  <Card className={`border-none rounded-xl relative overflow-hidden ${pullThrough >= 50 ? 'bg-[#111]' : 'bg-red-950/30'}`}>
                    <div className={`absolute top-0 left-0 w-1 h-full ${pullThrough >= 50 ? 'bg-green-500' : 'bg-red-500'}`} />
                    <CardContent className="py-4 px-5">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Pull Through %</p>
                      <p className={`text-2xl font-bold mt-1 ${pullThrough >= 50 ? 'text-green-400' : 'text-red-400'}`}>{pullThrough}%</p>
                      <button onClick={() => setShowWithdrawnDialog(true)} className="text-xs text-red-400 mt-0.5 hover:text-red-300 hover:underline transition-colors cursor-pointer" data-testid="withdrawn-drilldown-btn">{periodWithdrawn.length} withdrawn</button>
                    </CardContent>
                  </Card>
                </div>

                {/* Three-column breakdowns: Pipeline Status | Lead Source | Lender */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Pipeline Status */}
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
                        <PieChartIcon className="w-4 h-4 text-purple-500" />
                        Pipeline Status
                      </h3>
                      {statusData.length > 0 ? (
                        <div className="flex flex-col items-center">
                          <div className="relative w-[140px] h-[140px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={statusData} cx="50%" cy="50%" innerRadius={38} outerRadius={62} dataKey="value" strokeWidth={2} stroke="#111">
                                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '11px' }} formatter={(value, name) => [`${value} loans`, name]} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-xl font-bold text-white">{pullThrough}%</span>
                              <span className="text-[9px] text-gray-500 uppercase tracking-wider">Pull Thru</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1.5 mt-3 w-full">
                            {statusData.map(d => (
                              <div
                                key={d.name}
                                className={`flex items-center justify-between px-1 ${d.name === 'Withdrawn' ? 'cursor-pointer hover:bg-red-900/10 rounded transition-colors' : ''}`}
                                onClick={() => { if (d.name === 'Withdrawn') setShowWithdrawnDialog(true); }}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                  <span className="text-[11px] text-gray-400">{d.name}</span>
                                </div>
                                <span className="text-[11px] font-bold text-white">{d.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 text-sm">No loans</div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Lead Source */}
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
                        <Target className="w-4 h-4 text-purple-500" />
                        Lead Source
                      </h3>
                      {leadSourceData.length > 0 ? (
                        <div className="flex flex-col items-center">
                          <div className="relative w-[140px] h-[140px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={leadSourceData.map(([name, d], i) => ({ name, value: d.total, color: leadSourceColors[i % leadSourceColors.length] }))} cx="50%" cy="50%" innerRadius={38} outerRadius={62} dataKey="value" strokeWidth={2} stroke="#111">
                                  {leadSourceData.map(([, ], i) => <Cell key={i} fill={leadSourceColors[i % leadSourceColors.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '11px' }} formatter={(value, name) => [`${value} loans`, name]} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-xl font-bold text-white">{leadSourceData.reduce((s, [, d]) => s + d.total, 0)}</span>
                              <span className="text-[9px] text-gray-500 uppercase tracking-wider">Sources</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1.5 mt-3 w-full">
                            {leadSourceData.map(([name, data], i) => (
                              <div key={name} className="flex items-center justify-between px-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: leadSourceColors[i % leadSourceColors.length] }} />
                                  <span className="text-[11px] text-gray-400 truncate">{name}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-[11px] font-bold text-white">{data.total}</span>
                                  <span className="text-[10px] text-green-400">({data.closed})</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 text-sm">No data</div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Lender Distribution */}
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        Lender Distribution
                      </h3>
                      {sortedLenders.length > 0 ? (
                        <div className="space-y-3">
                          {sortedLenders.slice(0, 6).map(([name, data]) => {
                            const maxTotal = Math.max(...sortedLenders.map(([, d]) => d.total), 1);
                            const pct = (data.total / maxTotal) * 100;
                            return (
                              <div key={name}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[11px] text-gray-300 font-medium truncate">{name}</span>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-[10px] text-gray-500">{data.total}</span>
                                    <span className="text-[10px] text-green-400">{data.closed} closed</span>
                                  </div>
                                </div>
                                <div className="w-full h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                                  <div className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-400 transition-all" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 text-sm">No data</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* State Breakdown */}
                {sortedStates.length > 0 && (
                  <Card className="bg-[#111] border-none rounded-xl mt-2">
                    <CardContent className="p-0">
                      <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
                        <div>
                          <h2 className="text-base font-semibold text-white flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-purple-500" />
                            State Breakdown
                          </h2>
                          <p className="text-[11px] text-gray-500 mt-0.5">Pipeline & closings by state ({periodLabel})</p>
                        </div>
                        <Badge className="bg-[#1a1a1a] text-gray-400 border-[#333] text-xs">{sortedStates.length} States</Badge>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                        {/* State Donut — enhanced */}
                        <div className="p-6 border-r border-[#222] flex flex-col items-center justify-center">
                          <div className="relative w-[180px] h-[180px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={sortedStates.map(([name, d], i) => ({ name, value: d.total, color: stateColors[i % stateColors.length] }))} cx="50%" cy="50%" innerRadius={52} outerRadius={82} dataKey="value" strokeWidth={2} stroke="#111">
                                  {sortedStates.map(([, ], i) => <Cell key={i} fill={stateColors[i % stateColors.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '11px' }} formatter={(value, name) => [`${value} loans`, name]} />
                              </PieChart>
                            </ResponsiveContainer>
                            {/* Center label */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-2xl font-bold text-white">{sortedStates.reduce((s, [, d]) => s + d.total, 0)}</span>
                              <span className="text-[10px] text-gray-500 uppercase tracking-wider">Loans</span>
                            </div>
                          </div>
                          {/* Legend with counts */}
                          <div className="grid grid-cols-2 gap-x-5 gap-y-2 mt-4 w-full px-2">
                            {sortedStates.map(([name, data], i) => (
                              <div key={name} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: stateColors[i % stateColors.length] }} />
                                  <span className="text-xs text-gray-400">{name}</span>
                                </div>
                                <span className="text-xs font-bold text-white">{data.total}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* State Table */}
                        <div className="lg:col-span-2">
                          <table className="w-full" data-testid="state-breakdown-table">
                            <thead>
                              <tr className="border-b border-[#222]">
                                <th className="text-left py-2.5 px-5 text-gray-500 text-[11px] font-medium uppercase tracking-wider">State</th>
                                <th className="text-center py-2.5 px-3 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Total</th>
                                <th className="text-center py-2.5 px-3 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Closed</th>
                                <th className="text-center py-2.5 px-3 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Active</th>
                                <th className="text-right py-2.5 px-3 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Total Volume</th>
                                <th className="text-right py-2.5 px-5 text-gray-500 text-[11px] font-medium uppercase tracking-wider">Closed Volume</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedStates.map(([name, data], idx) => {
                                const maxTotal = Math.max(...sortedStates.map(([, d]) => d.total), 1);
                                const barWidth = (data.total / maxTotal) * 100;
                                return (
                                  <tr key={name} className={`border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/60 transition-colors ${idx === 0 && data.total > 0 ? 'bg-purple-900/5' : ''}`}>
                                    <td className="py-2.5 px-5">
                                      <div className="flex items-center gap-2.5">
                                        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white" style={{ backgroundColor: stateColors[idx % stateColors.length] }}>
                                          {name.slice(0, 2)}
                                        </div>
                                        <div className="min-w-0">
                                          <span className="text-white text-sm font-medium">{name}</span>
                                          <div className="w-16 h-1 bg-[#222] rounded-full mt-0.5">
                                            <div className="h-full rounded-full transition-all" style={{ width: `${barWidth}%`, backgroundColor: stateColors[idx % stateColors.length] }} />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                      <span className="text-sm font-bold text-white">{data.total}</span>
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                      <span className={`text-sm font-medium ${data.closed > 0 ? 'text-green-400' : 'text-gray-600'}`}>{data.closed}</span>
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                      <span className={`text-sm font-medium ${data.active > 0 ? 'text-amber-400' : 'text-gray-600'}`}>{data.active}</span>
                                    </td>
                                    <td className="py-2.5 px-3 text-right">
                                      <span className="text-xs text-gray-300">${data.volume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                                    </td>
                                    <td className="py-2.5 px-5 text-right">
                                      <span className={`text-xs ${data.closedVolume > 0 ? 'text-green-400' : 'text-gray-600'}`}>${data.closedVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {sortedStates.length > 0 && (
                              <tfoot>
                                <tr className="border-t-2 border-[#333] bg-[#0a0a0a]">
                                  <td className="py-2.5 px-5 text-white font-semibold text-sm">Total</td>
                                  <td className="py-2.5 px-3 text-center text-white font-bold">{periodLoans.length}</td>
                                  <td className="py-2.5 px-3 text-center text-green-400 font-bold">{periodClosed.length}</td>
                                  <td className="py-2.5 px-3 text-center text-amber-400 font-bold">{periodActive.length}</td>
                                  <td className="py-2.5 px-3 text-right text-white font-semibold text-xs">${periodLoans.reduce((s, l) => s + (l.loanAmount || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                                  <td className="py-2.5 px-5 text-right text-green-400 font-semibold text-xs">${periodClosedVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* LO Production — bottom of Sales Command Center */}
                <Card className="bg-[#111] border-none rounded-lg">
                  <CardContent className="p-0">
                    <div className="px-2.5 py-1.5 border-b border-[#1a1a1a] flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
                        <Trophy className="w-3 h-3 text-purple-500" />
                        Loan Officer Production
                        <span className="text-xs text-gray-500 font-normal ml-1">click to drill down</span>
                      </h2>
                      <span className="text-xs text-gray-500">{officersList.length} LOs</span>
                    </div>
                    <table className="w-full" data-testid="sales-lo-table">
                      <thead>
                        <tr className="border-b border-[#1a1a1a]">
                          <th className="text-left py-0.5 px-2 text-gray-500 text-xs font-medium uppercase w-5">#</th>
                          <th className="text-left py-0.5 px-1.5 text-gray-500 text-xs font-medium uppercase">Name</th>
                          <th className="text-center py-0.5 px-1.5 text-gray-500 text-xs font-medium uppercase">Subs</th>
                          <th className="text-center py-0.5 px-1.5 text-gray-500 text-xs font-medium uppercase">Active</th>
                          <th className="text-center py-0.5 px-1.5 text-gray-500 text-xs font-medium uppercase">Closings</th>
                          <th className="text-right py-0.5 px-1.5 text-gray-500 text-xs font-medium uppercase">Cls Vol</th>
                          <th className="text-right py-0.5 px-2 text-gray-500 text-xs font-medium uppercase">Pipe Vol</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLOs.map(([name, data], idx) => {
                          const isKnownLO = officersList.find(o => o.name === name);
                          const rankColors = ['text-yellow-400', 'text-gray-300', 'text-amber-600'];
                          return (
                            <tr
                              key={name}
                              className={`border-b border-[#141414] transition-colors ${isKnownLO ? 'hover:bg-purple-900/10 cursor-pointer' : 'hover:bg-[#161616]'} ${idx === 0 && data.closed > 0 ? 'bg-purple-900/5' : ''}`}
                              onClick={() => { if (isKnownLO) setSelectedOfficer(isKnownLO.id); }}
                              data-testid={`sales-lo-row-${name.replace(/\s/g, '-').toLowerCase()}`}
                            >
                              <td className="py-[2px] px-2 text-sm font-bold"><span className={idx < 3 ? rankColors[idx] : 'text-gray-500'}>{idx + 1}</span></td>
                              <td className="py-[2px] px-1.5">
                                <span className="text-white text-sm font-medium truncate">{name}</span>
                              </td>
                              <td className="py-[2px] px-1.5 text-center"><span className={`text-sm ${data.withdrawn > 0 ? 'text-red-400' : 'text-gray-600'}`}>{data.withdrawn}</span></td>
                              <td className="py-[2px] px-1.5 text-center"><span className={`text-sm ${data.active > 0 ? 'text-amber-400' : 'text-gray-600'}`}>{data.active}</span></td>
                              <td className="py-[2px] px-1.5 text-center"><span className={`text-sm font-bold ${data.closed > 0 ? 'text-green-400' : 'text-gray-600'}`}>{data.closed}</span></td>
                              <td className="py-[2px] px-1.5 text-right text-sm text-gray-300">${data.closedVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                              <td className="py-[2px] px-2 text-right text-sm text-gray-300">${data.activeVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {sortedLOs.length > 0 && (
                        <tfoot>
                          <tr className="border-t border-[#282828] bg-[#0a0a0a]">
                            <td className="py-[2px] px-2"></td>
                            <td className="py-[2px] px-1.5 text-white font-semibold text-sm">Total</td>
                            <td className="py-[2px] px-1.5 text-center text-red-400 font-bold text-sm">{periodWithdrawn.length}</td>
                            <td className="py-[2px] px-1.5 text-center text-amber-400 font-bold text-sm">{periodActive.length}</td>
                            <td className="py-[2px] px-1.5 text-center text-green-400 font-bold text-sm">{periodClosed.length}</td>
                            <td className="py-[2px] px-1.5 text-right text-white font-semibold text-sm">${periodClosedVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                            <td className="py-[2px] px-2 text-right text-white font-semibold text-sm">${periodActiveVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </CardContent>
                </Card>

                {/* Withdrawn Loans Drill-Down Dialog */}
                <Dialog open={showWithdrawnDialog} onOpenChange={setShowWithdrawnDialog}>
                  <DialogContent className="bg-[#111] border-[#222] text-white max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-white text-lg flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-400" />
                        Withdrawn Loans — {periodWithdrawn.length} Total
                      </DialogTitle>
                      <DialogDescription className="text-gray-400 text-sm">
                        All withdrawn loans for the selected period
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2">
                      {periodWithdrawn.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No withdrawn loans in this period</p>
                      ) : (
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#333]">
                              <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium uppercase">Broker</th>
                              <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium uppercase">Client</th>
                              <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium uppercase">Loan #</th>
                              <th className="text-right py-2 px-2 text-gray-400 text-xs font-medium uppercase">Amount</th>
                              <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium uppercase">Lead Source</th>
                              <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium uppercase">Lender</th>
                              <th className="text-center py-2 px-2 text-gray-400 text-xs font-medium uppercase">Close Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {periodWithdrawn.map((loan, idx) => (
                              <tr key={loan.id || idx} className="border-b border-[#1a1a1a] hover:bg-red-900/5 transition-colors">
                                <td className="py-2 px-2 text-white text-sm">{loan.userName || '—'}</td>
                                <td className="py-2 px-2 text-gray-300 text-sm">{loan.clientName || '—'}</td>
                                <td className="py-2 px-2 text-gray-300 text-sm">{loan.loanNumber || '—'}</td>
                                <td className="py-2 px-2 text-right text-gray-300 text-sm">${(loan.loanAmount || 0).toLocaleString()}</td>
                                <td className="py-2 px-2 text-sm">
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${loan.leadSource === 'Referral' ? 'bg-amber-900/30 text-amber-400' : 'bg-[#1a1a1a] text-gray-400'}`}>
                                    {loan.leadSource || '—'}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-gray-300 text-sm">{loan.lender || '—'}</td>
                                <td className="py-2 px-2 text-center text-gray-400 text-sm">{loan.date ? new Date(loan.date + 'T12:00:00').toLocaleDateString() : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-[#333] bg-[#0a0a0a]">
                              <td className="py-2 px-2 text-white font-semibold text-sm" colSpan={3}>Total: {periodWithdrawn.length} loans</td>
                              <td className="py-2 px-2 text-right text-red-400 font-bold text-sm">${periodWithdrawn.reduce((s, l) => s + (l.loanAmount || 0), 0).toLocaleString()}</td>
                              <td colSpan={3}></td>
                            </tr>
                          </tfoot>
                        </table>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            );
          })()}
        </>
      ) :
      isAdmin && viewingProcessor ? (
        <>
          {/* Processor-style Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-2xl lg:text-3xl font-semibold text-white">
                {viewingProcessorName}'s Pipeline
              </h1>
              <p className="text-gray-400 text-sm">Processing pipeline and workload overview</p>
            </div>
            <Badge className="bg-teal-900/50 text-teal-400 border border-teal-700 text-sm px-3 py-1.5">
              Processor
            </Badge>
          </div>

          {/* Month / YTD Selector */}
          <Card className="bg-[#111] border-none rounded-xl">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                {viewMode === 'month' ? (
                  <>
                    <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">{formatMonth(selectedMonth)}</div>
                      <p className="text-xs text-gray-500 mt-1">Processing period</p>
                    </div>
                    <Button variant="outline" size="icon" onClick={() => navigateMonth(1)} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="icon" onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() - 1, selectedMonth.getMonth(), 1))} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">{selectedMonth.getFullYear()}</div>
                      <p className="text-xs text-gray-500 mt-1">Jan – Dec {selectedMonth.getFullYear()}</p>
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() + 1, selectedMonth.getMonth(), 1))} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10">
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </>
                )}
              </div>
              {/* Monthly / YTD Toggle */}
              <div className="flex justify-center mt-3">
                <div className="bg-[#0a0a0a] rounded-lg p-1 flex gap-1">
                  <button
                    onClick={() => setViewMode('month')}
                    data-testid="admin-proc-view-mode-month"
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'month'
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setViewMode('ytd')}
                    data-testid="admin-proc-view-mode-ytd"
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'ytd'
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    YTD
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {(() => {
            // Calculate processor-specific stats
            const targetMonth = selectedMonth.getMonth();
            const targetYear = selectedMonth.getFullYear();
            const myAssignedLoans = loans.filter(l => l.processor === viewingProcessorName);
            const monthAssigned = myAssignedLoans.filter(l => {
              const d = parseLocalDate(l.date);
              return d.getMonth() === targetMonth && d.getFullYear() === targetYear;
            });
            const ytdAssigned = myAssignedLoans.filter(l => {
              const d = parseLocalDate(l.date);
              return d.getFullYear() === targetYear;
            });
            const activeAssigned = viewMode === 'ytd' ? ytdAssigned : monthAssigned;
            const assignedInProcess = activeAssigned.filter(l => l.status === 'In Process' || l.status === 'Pending');
            const assignedClosed = activeAssigned.filter(l => l.status === 'Closed');
            const assignedWithdrawn = activeAssigned.filter(l => l.status === 'Withdrawn');
            const assignedVolume = activeAssigned.reduce((sum, l) => sum + (l.loanAmount || 0), 0);
            const closedVolume = assignedClosed.reduce((sum, l) => sum + (l.loanAmount || 0), 0);
            const allTimeActive = myAssignedLoans.filter(l => l.status === 'In Process' || l.status === 'Pending').length;
            const allTimeClosed = myAssignedLoans.filter(l => l.status === 'Closed').length;
            const allTimeVolume = myAssignedLoans.filter(l => l.status === 'Closed').reduce((sum, l) => sum + (l.loanAmount || 0), 0);

            return (
              <>
                {/* Pipeline Stats */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-teal-500" />
                    Pipeline
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[24px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                    <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-teal-500">
                      <CardContent className="p-[24px]">
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-400">Assigned</span>
                          <div className="text-2xl font-bold text-teal-400">{activeAssigned.length}</div>
                          <p className="text-xs text-gray-500">{viewMode === 'ytd' ? `${targetYear} YTD` : 'This month'}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-amber-500">
                      <CardContent className="p-[24px]">
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-400">In Process</span>
                          <div className="text-2xl font-bold text-amber-400">{assignedInProcess.length}</div>
                          <p className="text-xs text-gray-500">Active loans</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-green-500">
                      <CardContent className="p-[24px]">
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-400">Closed</span>
                          <div className="text-2xl font-bold text-green-400">{assignedClosed.length}</div>
                          <p className="text-xs text-gray-500">{formatCurrency(closedVolume)}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-red-500">
                      <CardContent className="p-[24px]">
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-400">Withdrawn</span>
                          <div className="text-2xl font-bold text-red-400">{assignedWithdrawn.length}</div>
                          <p className="text-xs text-gray-500">{viewMode === 'ytd' ? `${targetYear} YTD` : 'This month'}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* All-Time Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-[24px]">
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-[24px]">
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-gray-400">Total Active Loans</span>
                        <div className="text-2xl font-bold text-white">{allTimeActive}</div>
                        <p className="text-xs text-gray-500">Currently in process</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-[24px]">
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-gray-400">All-Time Closed</span>
                        <div className="text-2xl font-bold text-green-400">{allTimeClosed}</div>
                        <p className="text-xs text-gray-500">Successfully closed</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardContent className="p-[24px]">
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-gray-400">All-Time Volume</span>
                        <div className="text-2xl font-bold text-white">{formatCurrency(allTimeVolume)}</div>
                        <p className="text-xs text-gray-500">Closed loan volume</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Pipeline Breakdown Chart + Processing Metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px]">
                  {/* Donut Chart */}
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <PieChartIcon className="w-4 h-4 text-teal-500" />
                        Pipeline Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-[24px] pt-0">
                      {activeAssigned.length > 0 ? (() => {
                        const statusData = [
                          { name: 'In Process', value: assignedInProcess.length, color: '#f59e0b' },
                          { name: 'Closed', value: assignedClosed.length, color: '#22c55e' },
                          { name: 'Withdrawn', value: assignedWithdrawn.length, color: '#ef4444' },
                        ].filter(d => d.value > 0);
                        return (
                          <div className="flex items-center gap-6">
                            <div className="w-[140px] h-[140px] flex-shrink-0">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" strokeWidth={0}>
                                    {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="flex flex-col gap-3">
                              {statusData.map(d => (
                                <div key={d.name} className="flex items-center gap-3">
                                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                  <span className="text-sm text-gray-400">{d.name}</span>
                                  <span className="text-sm font-bold text-white">{d.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })() : (
                        <div className="text-center py-6">
                          <PieChartIcon className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">No loans {viewMode === 'ytd' ? 'this year' : 'this month'}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Turnaround Time + Alerts */}
                  <Card className="bg-[#111] border-none rounded-xl">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Clock className="w-4 h-4 text-teal-500" />
                        Processing Metrics
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-[24px] pt-0">
                      {(() => {
                        const closedWithDates = myAssignedLoans.filter(l => l.status === 'Closed' && l.submissionDate && l.date);
                        let avgTurnaround = 0;
                        if (closedWithDates.length > 0) {
                          const totalDays = closedWithDates.reduce((sum, l) => {
                            const sub = parseLocalDate(l.submissionDate);
                            const close = parseLocalDate(l.date);
                            return sum + Math.max(0, Math.round((close - sub) / (1000 * 60 * 60 * 24)));
                          }, 0);
                          avgTurnaround = Math.round(totalDays / closedWithDates.length);
                        }
                        const today = new Date();
                        const agedLoans = activeAssigned.filter(l => {
                          if (l.status !== 'In Process' && l.status !== 'Pending') return false;
                          const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
                          const days = Math.round((today - sub) / (1000 * 60 * 60 * 24));
                          return days >= 25;
                        });

                        return (
                          <div className="space-y-5">
                            <div className="grid grid-cols-2 gap-[24px]">
                              <div className="bg-[#0a0a0a] rounded-lg p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg Turnaround</p>
                                <p className="text-2xl font-bold text-teal-400">{closedWithDates.length > 0 ? `${avgTurnaround}d` : '—'}</p>
                                <p className="text-xs text-gray-500 mt-1">Submission to close</p>
                              </div>
                              <div
                                className={`bg-[#0a0a0a] rounded-lg p-4 ${agedLoans.length > 0 ? 'cursor-pointer hover:bg-[#151515] transition-colors' : ''}`}
                                onClick={() => {
                                  if (agedLoans.length > 0) {
                                    const enriched = agedLoans.map(l => {
                                      const sub = l.submissionDate ? parseLocalDate(l.submissionDate) : parseLocalDate(l.date);
                                      const days = Math.round((new Date() - sub) / (1000 * 60 * 60 * 24));
                                      return { ...l, daysAged: days };
                                    });
                                    setAgedLoansData(enriched);
                                    setShowAgedLoansDialog(true);
                                  }
                                }}
                                data-testid="aged-loans-clickable-admin"
                              >
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Aged Loans</p>
                                <p className={`text-2xl font-bold ${agedLoans.length > 0 ? 'text-red-400' : 'text-green-400'}`}>{agedLoans.length}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {agedLoans.length > 0 ? 'Click to view details' : '25+ days in process'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Assigned Loans Table */}
                <Card className="bg-[#111] border-none rounded-xl">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-500" />
                      Assigned Loans
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {activeAssigned.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#333]">
                              <th className="text-left py-2 px-3 text-gray-400 font-medium">Client</th>
                              <th className="text-left py-2 px-3 text-gray-400 font-medium">Loan Officer</th>
                              <th className="text-left py-2 px-3 text-gray-400 font-medium">Lender</th>
                              <th className="text-right py-2 px-3 text-gray-400 font-medium">Amount</th>
                              <th className="text-center py-2 px-3 text-gray-400 font-medium">Status</th>
                              <th className="text-center py-2 px-3 text-gray-400 font-medium">Close Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeAssigned.map((loan, idx) => (
                              <tr key={loan.id} className={`border-b border-[#222] ${idx % 2 === 0 ? 'bg-[#0d0d0d]' : ''} ${loan.status === 'Withdrawn' ? 'opacity-50' : ''}`}>
                                <td className="py-2.5 px-3 text-white font-medium">{loan.clientName}</td>
                                <td className="py-2.5 px-3 text-gray-300">{loan.userName}</td>
                                <td className="py-2.5 px-3 text-gray-300">{loan.lender || '—'}</td>
                                <td className="py-2.5 px-3 text-white text-right">{formatCurrency(loan.loanAmount)}</td>
                                <td className="py-2.5 px-3 text-center">
                                  <button onClick={() => handleDashboardStatusClick(loan)} className="cursor-pointer" data-testid={`dash-admin-status-btn-${loan.id}`}>
                                    <Badge className={
                                      loan.status === 'Closed' ? 'bg-green-900/50 text-green-400 border border-green-800 hover:brightness-125 transition-all' :
                                      loan.status === 'Withdrawn' ? 'bg-red-900/50 text-red-400 border border-red-800 hover:brightness-125 transition-all' :
                                      'bg-amber-900/50 text-amber-400 border border-amber-800 hover:brightness-125 transition-all'
                                    }>
                                      {loan.status === 'Pending' ? 'In Process' : loan.status}
                                    </Badge>
                                  </button>
                                </td>
                                <td className="py-2.5 px-3 text-center text-gray-300">{formatDate(loan.date)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-[#444] bg-[#1a1a1a]">
                              <td className="py-2.5 px-3 text-white font-bold" colSpan="3">Total ({activeAssigned.length} loans)</td>
                              <td className="py-2.5 px-3 text-white text-right font-bold">{formatCurrency(assignedVolume)}</td>
                              <td colSpan="2"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                        <p className="text-gray-500">No loans assigned to {viewingProcessorName} {viewMode === 'ytd' ? 'this year' : 'this month'}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            );
          })()}

          {/* Aged Loans Dialog (Admin viewing processor) */}
          <Dialog open={showAgedLoansDialog} onOpenChange={setShowAgedLoansDialog}>
            <DialogContent className="bg-[#111] border-[#333] text-white max-w-3xl w-[90vw]">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Aged Loans — 25+ Days in Process
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  {agedLoansData.length} loan{agedLoansData.length !== 1 ? 's' : ''} have been in process for over 25 days
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
                {agedLoansData.map(loan => (
                  <div key={loan.id} className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-semibold">{loan.clientName}</span>
                      <Badge className="bg-red-900/50 text-red-400 border border-red-800">{loan.daysAged}d aged</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">LO:</span> <span className="text-gray-300">{loan.userName}</span></div>
                      <div><span className="text-gray-500">Lender:</span> <span className="text-gray-300">{loan.lender || '—'}</span></div>
                      <div><span className="text-gray-500">Amount:</span> <span className="text-gray-300">{formatCurrency(loan.loanAmount)}</span></div>
                      <div><span className="text-gray-500">Loan #:</span> <span className="text-gray-300">{loan.loanNumber}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </>
      ) : (
      <>

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl lg:text-3xl font-semibold text-white">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-gray-400 text-sm">
            Track your commission performance and manage your loans
          </p>
        </div>
        {brokerType && (
          <Badge className="bg-[#1a1a1a] text-gray-300 border border-[#333] text-sm px-3 py-1.5">
            {getBrokerTypeDisplay(brokerType)}
          </Badge>
        )}
      </div>

      {/* Period Selector */}
      <Card className="bg-[#111] border-none rounded-xl">
        <CardContent className="px-[24px] py-4">
          <div className="flex items-center justify-between">
            {viewMode === 'month' ? (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigateMonth(-1)}
                  className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{formatMonth(selectedMonth)}</div>
                  <p className="text-xs text-gray-500 mt-1">Commission period</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigateMonth(1)}
                  className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() - 1, selectedMonth.getMonth(), 1))}
                  className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{selectedMonth.getFullYear()}</div>
                  <p className="text-xs text-gray-500 mt-1">Jan – Dec {selectedMonth.getFullYear()}</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() + 1, selectedMonth.getMonth(), 1))}
                  className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300 h-10 w-10"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
          {/* Monthly / YTD Toggle */}
          <div className="flex justify-center mt-3">
            <div className="bg-[#0a0a0a] rounded-lg p-1 flex gap-1">
              <button
                onClick={() => setViewMode('month')}
                data-testid="view-mode-month"
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'month'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setViewMode('ytd')}
                data-testid="view-mode-ytd"
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'ytd'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                YTD
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[24px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-green-500">
          <CardContent className="p-[24px]">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-400">Earnings</span>
                <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-green-500" />
                </div>
              </div>
              <div className="text-2xl font-bold text-green-400">{formatCurrency(viewMode === 'ytd' ? ytdStats.earnings : stats.monthEarnings)}</div>
              <p className="text-xs text-gray-500">{viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : formatMonth(selectedMonth)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-blue-500">
          <CardContent className="p-[24px]">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-400">Closed Loans</span>
                <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4 text-blue-500" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">{viewMode === 'ytd' ? ytdStats.closedLoans : stats.monthClosedLoans}</div>
              {stats.currentTier && (
                <Badge className="bg-green-900/50 text-green-400 border border-green-800">
                  {stats.currentTier.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-amber-500">
          <CardContent className="p-[24px]">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-400">Projected</span>
                <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                </div>
              </div>
              <div className="text-2xl font-bold text-amber-400">{formatCurrency(viewMode === 'ytd' ? ytdStats.projectedCommission : stats.projectedCommission)}</div>
              <p className="text-xs text-gray-500">{viewMode === 'ytd' ? ytdStats.inProcessCount : stats.inProcessCount} loan{(viewMode === 'ytd' ? ytdStats.inProcessCount : stats.inProcessCount) !== 1 ? 's' : ''} in process</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-emerald-500">
          <CardContent className="p-[24px]">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-400">Loan Volume</span>
                <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <Banknote className="w-4 h-4 text-emerald-500" />
                </div>
              </div>
              <div className="text-2xl font-bold text-emerald-400">{formatCurrency(viewMode === 'ytd' ? ytdStats.volume : stats.monthVolume)}</div>
              <p className="text-xs text-gray-500">{viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : formatMonth(selectedMonth)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px]">
        <Card className="bg-[#111] border-none rounded-xl">
          <CardHeader>
            <CardTitle className="text-white">Quick Calculate</CardTitle>
            <CardDescription className="text-gray-500">
              Calculate your commission for upcoming loans
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/commission/calculator')}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              size="lg"
            >
              Open Calculator
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#222]">
            <h3 className="text-white font-bold tracking-wide text-sm uppercase flex items-center gap-3">
              <div className="w-1 h-6 bg-green-500 rounded-full" />
              {viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : formatMonth(selectedMonth)} Closings
            </h3>
            <p className="text-gray-500 text-xs mt-1 ml-4">Closed loans for the selected {viewMode === 'ytd' ? 'year' : 'month'}</p>
          </div>
          <CardContent className="p-0">
            {(viewMode === 'ytd' ? ytdStats.recentLoans : stats.recentLoans).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#222]">
                      <th className="text-left py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider">Client</th>
                      <th className="text-right py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider">Amount</th>
                      <th className="text-center py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider">Date</th>
                      <th className="text-center py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewMode === 'ytd' ? ytdStats.recentLoans : stats.recentLoans).map((loan, index) => {
                      const loanDate = parseLocalDate(loan.date);
                      const formattedDate = loanDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return (
                        <tr key={loan.id} className="border-b border-[#222] hover:bg-[#1a1a1a] transition-colors">
                          <td className="py-3.5 px-6 text-white font-medium">{loan.clientName}</td>
                          <td className="py-3.5 px-6 text-right text-green-400 font-semibold">{formatCurrency(loan.loanAmount)}</td>
                          <td className="py-3.5 px-6 text-center text-gray-300 text-sm">{formattedDate}</td>
                          <td className="py-3.5 px-6 text-center">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-900/30 border border-green-800/50 text-green-400 text-xs font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                              Closed
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#1a1a1a] border-t border-[#333]">
                      <td className="py-3.5 px-6 text-white font-bold">Total</td>
                      <td className="py-3.5 px-6 text-right text-green-400 font-bold">
                        {formatCurrency((viewMode === 'ytd' ? ytdStats.recentLoans : stats.recentLoans).reduce((sum, l) => sum + (l.loanAmount || 0), 0))}
                      </td>
                      <td className="py-3.5 px-6 text-center text-gray-400 text-sm">
                        {(viewMode === 'ytd' ? ytdStats.recentLoans : stats.recentLoans).length} loan{(viewMode === 'ytd' ? ytdStats.recentLoans : stats.recentLoans).length !== 1 ? 's' : ''}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No closed loans in {viewMode === 'ytd' ? selectedMonth.getFullYear() : formatMonth(selectedMonth)}</p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/commission/loans')}
                  className="mt-3 border-[#333] text-gray-300 hover:bg-[#1a1a1a]"
                >
                  Add Loans
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPI Reporting Section */}
      <div className="mt-8 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-green-500" />
            Performance KPIs
          </h2>
          <span className="text-sm text-gray-500">All-time metrics</span>
        </div>

        {/* All-Time Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-[24px]">
          <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-blue-500">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-400">Loans Closed</span>
                  <div className="w-7 h-7 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Target className="w-3.5 h-3.5 text-blue-500" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-blue-400">{kpis.allTimeLoans}</div>
                <p className="text-xs text-gray-500">All time</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-purple-500">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-400">Total Volume</span>
                  <div className="w-7 h-7 bg-purple-500/10 rounded-lg flex items-center justify-center">
                    <Banknote className="w-3.5 h-3.5 text-purple-500" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-purple-400">{formatCurrency(kpis.allTimeVolume)}</div>
                <p className="text-xs text-gray-500">All time</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-cyan-500">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-400">Avg Loan Size</span>
                  <div className="w-7 h-7 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-3.5 h-3.5 text-cyan-500" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-cyan-400">{formatCurrency(kpis.avgLoanSize)}</div>
                <p className="text-xs text-gray-500">Per loan</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-amber-500">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-400">In Process</span>
                  <div className="w-7 h-7 bg-amber-500/10 rounded-lg flex items-center justify-center">
                    <Calendar className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-amber-400">{kpis.inProcessLoans}</div>
                <p className="text-xs text-gray-500">Loans</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-0 rounded-xl border-l-4 border-l-amber-600">
            <CardContent className="p-[24px]">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-400">Pipeline Volume</span>
                  <div className="w-7 h-7 bg-amber-500/10 rounded-lg flex items-center justify-center">
                    <Banknote className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-amber-400">{formatCurrency(kpis.inProcessVolume)}</div>
                <p className="text-xs text-gray-500">In process</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Self-Gen vs Company & Conversion */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[24px]">
          <Card className="bg-[#111] border-[#222] cursor-pointer hover:border-green-800 transition-colors" onClick={() => setShowDetailDialog('selfGen')} data-testid="self-gen-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-green-500" />
                Self-Generated
                <span className="text-xs text-gray-500 ml-auto">Click for details</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Period Toggle */}
                <div className="flex bg-[#1a1a1a] rounded-lg p-1">
                  {[
                    { key: 'last30Days', label: '30 Days' },
                    { key: 'quarterly', label: 'Quarterly' },
                    { key: 'ytd', label: 'YTD' },
                    { key: 'lifetime', label: 'Lifetime' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={(e) => { e.stopPropagation(); setSelfGenPeriod(key); }}
                      className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${
                        selfGenPeriod === key
                          ? 'bg-green-600 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      data-testid={`self-gen-period-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                
                {/* Stats */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Loans</span>
                    <span className="text-white font-semibold">{kpis.selfGenByPeriod?.[selfGenPeriod]?.loans || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Earnings</span>
                    <span className="text-green-400 font-semibold">{formatCurrency(kpis.selfGenByPeriod?.[selfGenPeriod]?.earnings || 0)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-[#222] cursor-pointer hover:border-blue-800 transition-colors" onClick={() => setShowDetailDialog('company')} data-testid="company-leads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                Company Leads
                <span className="text-xs text-gray-500 ml-auto">Click for details</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Period Toggle */}
                <div className="flex bg-[#1a1a1a] rounded-lg p-1">
                  {[
                    { key: 'last30Days', label: '30 Days' },
                    { key: 'quarterly', label: 'Quarterly' },
                    { key: 'ytd', label: 'YTD' },
                    { key: 'lifetime', label: 'Lifetime' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={(e) => { e.stopPropagation(); setCompanyPeriod(key); }}
                      className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${
                        companyPeriod === key
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      data-testid={`company-period-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                
                {/* Stats */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Loans</span>
                    <span className="text-white font-semibold">{kpis.companyByPeriod?.[companyPeriod]?.loans || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Earnings</span>
                    <span className="text-blue-400 font-semibold">{formatCurrency(kpis.companyByPeriod?.[companyPeriod]?.earnings || 0)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-none rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-green-500" />
                Pull Through %
              </CardTitle>
              <CardDescription className="text-gray-500 text-xs">
                Closed / (Closed + Withdrawn)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Period Toggle */}
                <div className="flex bg-[#1a1a1a] rounded-lg p-1">
                  {[
                    { key: 'last30Days', label: '30 Days' },
                    { key: 'quarterly', label: 'Quarterly' },
                    { key: 'ytd', label: 'YTD' },
                    { key: 'lifetime', label: 'Lifetime' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setPullThroughPeriod(key)}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-all ${
                        pullThroughPeriod === key
                          ? 'bg-green-600 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                
                {/* Value Display */}
                <div className="text-center py-2">
                  <div className="text-4xl font-bold text-green-400">
                    {kpis.pullThrough[pullThroughPeriod].toFixed(1)}%
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {pullThroughPeriod === 'last30Days' && 'Last 30 Days'}
                    {pullThroughPeriod === 'quarterly' && 'This Quarter'}
                    {pullThroughPeriod === 'ytd' && 'Year to Date'}
                    {pullThroughPeriod === 'lifetime' && 'All Time'}
                  </p>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-[#222] rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-600 to-green-400 h-3 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(kpis.pullThrough[pullThroughPeriod], 100)}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lead Source & Lender Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px]">
          {/* Lead Source */}
          <Card className="bg-[#111] border-none rounded-xl">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-500" />
                Lead Source
              </CardTitle>
              <CardDescription className="text-gray-500">
                Breakdown by lead source
              </CardDescription>
            </CardHeader>
            <CardContent>
              {kpis.leadSourceBreakdown.length > 0 ? (
                <div className="space-y-3">
                  {kpis.leadSourceBreakdown.map((item, index) => (
                    <div key={item.source} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">{item.source}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-400">{item.count} loans</span>
                          <span className="text-green-400 font-medium">{formatCurrency(item.earnings)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-[#222] rounded-full h-1.5">
                        <div 
                          className="bg-purple-500 h-1.5 rounded-full" 
                          style={{ 
                            width: `${kpis.allTimeLoans > 0 ? (item.count / kpis.allTimeLoans) * 100 : 0}%`,
                            opacity: 1 - (index * 0.15)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No lead source data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Lender Mix */}
          <Card className="bg-[#111] border-none rounded-xl">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Lender Mix
              </CardTitle>
              <CardDescription className="text-gray-500">
                Breakdown by lender
              </CardDescription>
            </CardHeader>
            <CardContent>
              {kpis.lenderBreakdown.length > 0 ? (
                <div className="space-y-3">
                  {kpis.lenderBreakdown.map((item, index) => (
                    <div key={item.lender} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">{item.lender}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-400">{item.count} loans</span>
                          <span className="text-blue-400 font-medium">{formatCurrency(item.volume)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-[#222] rounded-full h-1.5">
                        <div 
                          className="bg-blue-500 h-1.5 rounded-full" 
                          style={{ 
                            width: `${kpis.allTimeLoans > 0 ? (item.count / kpis.allTimeLoans) * 100 : 0}%`,
                            opacity: 1 - (index * 0.15)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No lender data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Leaderboard */}
        <Card className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-white font-bold tracking-wide text-sm uppercase">
                {viewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : formatMonth(selectedMonth)} Closings
              </h3>
            </div>
            <span className="text-xs text-gray-500">
              {leaderboard.reduce((sum, e) => sum + e.loan_count, 0)} total units
            </span>
          </div>
          {leaderboard.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="closings-leaderboard-table">
                <thead>
                  <tr className="border-b border-[#222]">
                    <th className="text-left py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider w-[50%]">Broker</th>
                    <th className="text-right py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider w-[35%]">Volume</th>
                    <th className="text-center py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider w-[15%]">Units</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry) => {
                    const maxVolume = leaderboard[0]?.total_volume || 1;
                    const isTop = entry.total_volume > 0 && entry.total_volume >= maxVolume * 0.5;
                    const isZero = entry.total_volume === 0;
                    return (
                      <tr 
                        key={entry.user_id} 
                        className={`border-b border-[#222] transition-colors hover:brightness-125 ${
                          isZero 
                            ? 'bg-red-950/20 border-l-4 border-l-red-500' 
                            : isTop 
                            ? 'bg-green-950/20 border-l-4 border-l-green-500' 
                            : 'bg-yellow-950/15 border-l-4 border-l-yellow-500'
                        }`}
                      >
                        <td className="py-3.5 px-6">
                          <span className="text-white font-medium">{entry.user_name}</span>
                        </td>
                        <td className="py-3.5 px-6 text-right">
                          <span className={`font-semibold ${isZero ? 'text-red-400' : isTop ? 'text-green-400' : 'text-yellow-400'}`}>
                            ${entry.total_volume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td className="py-3.5 px-6 text-center">
                          <span className={`font-bold text-lg ${isZero ? 'text-red-400' : entry.loan_count >= 2 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {entry.loan_count}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#1a1a1a] border-t border-[#333]">
                    <td className="py-3.5 px-6">
                      <span className="text-white font-bold">Total:</span>
                    </td>
                    <td className="py-3.5 px-6 text-right">
                      <span className="text-green-400 font-bold">
                        ${leaderboard.reduce((sum, e) => sum + e.total_volume, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3.5 px-6 text-center">
                      <span className="text-white font-bold text-lg">
                        {leaderboard.reduce((sum, e) => sum + e.loan_count, 0)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Trophy className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">No closed loans this month yet</p>
              <p className="text-gray-600 text-sm mt-1">Be the first on the leaderboard!</p>
            </div>
          )}
        </Card>

        {/* Monthly Trends - Compact Bar Chart */}
        <Card className="bg-[#111] border-none rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-green-500" />
              Monthly Trends
            </CardTitle>
            <CardDescription className="text-gray-500">
              Jan - Dec {new Date().getFullYear()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[180px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kpis.monthlyTrends} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    interval={0}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    cursor={{ fill: 'rgba(34, 197, 94, 0.1)' }}
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value, name) => {
                      if (name === 'loans') return [<span key="loans" className="text-white">{value}</span>, 'Loans'];
                      if (name === 'earnings') return [<span key="earnings" className="text-green-400">{formatCurrency(value)}</span>, 'Earnings'];
                      return [value, name];
                    }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Bar 
                    dataKey="loans" 
                    radius={[3, 3, 0, 0]}
                    maxBarSize={24}
                  >
                    {kpis.monthlyTrends.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.loans === 0 ? '#1a3a1a' : entry.isDecline ? '#ef4444' : '#22c55e'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Summary row below chart */}
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#222] text-sm">
              <div className="text-gray-400">
                Total: <span className="text-white font-medium">{kpis.monthlyTrends.reduce((sum, m) => sum + m.loans, 0)} loans</span>
              </div>
              <div className="text-gray-400">
                Earnings: <span className="text-green-400 font-medium">{formatCurrency(kpis.monthlyTrends.reduce((sum, m) => sum + m.earnings, 0))}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Self-Gen / Company Detail Dialog */}
      <Dialog open={!!showDetailDialog} onOpenChange={() => setShowDetailDialog(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              {showDetailDialog === 'selfGen' ? (
                <><Users className="w-5 h-5 text-green-500" /> Self-Generated Loans</>
              ) : (
                <><Building2 className="w-5 h-5 text-blue-500" /> Company Lead Loans</>
              )}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {showDetailDialog === 'selfGen'
                ? `${kpis.selfGenByPeriod?.[selfGenPeriod]?.loans || 0} loans — ${selfGenPeriod === 'last30Days' ? 'Last 30 Days' : selfGenPeriod === 'quarterly' ? 'This Quarter' : selfGenPeriod === 'ytd' ? 'Year to Date' : 'All Time'}`
                : `${kpis.companyByPeriod?.[companyPeriod]?.loans || 0} loans — ${companyPeriod === 'last30Days' ? 'Last 30 Days' : companyPeriod === 'quarterly' ? 'This Quarter' : companyPeriod === 'ytd' ? 'Year to Date' : 'All Time'}`
              }
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const isSelfGen = showDetailDialog === 'selfGen';
            const periodData = isSelfGen
              ? kpis.selfGenByPeriod?.[selfGenPeriod]
              : kpis.companyByPeriod?.[companyPeriod];
            const loanList = (periodData?.loanDetails || []).sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date));
            
            return loanList.length > 0 ? (
              <div className="space-y-1 mt-2">
                {/* Summary bar */}
                <div className={`flex justify-between items-center px-4 py-3 rounded-lg mb-3 ${isSelfGen ? 'bg-green-900/20 border border-green-800/30' : 'bg-blue-900/20 border border-blue-800/30'}`}>
                  <div>
                    <span className="text-gray-400 text-sm">Total Earnings</span>
                    <div className={`text-xl font-bold ${isSelfGen ? 'text-green-400' : 'text-blue-400'}`}>{formatCurrency(periodData?.earnings || 0)}</div>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-400 text-sm">Volume</span>
                    <div className="text-lg font-semibold text-white">
                      {formatCurrency(loanList.reduce((sum, l) => sum + (l.loanAmount || 0), 0))}
                    </div>
                  </div>
                </div>
                {/* Loan table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="detail-loans-table">
                    <thead>
                      <tr className="border-b border-[#333]">
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Client</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Lender</th>
                        <th className="text-right py-2 px-3 text-gray-400 font-medium">Amount</th>
                        <th className="text-center py-2 px-3 text-gray-400 font-medium">Close Date</th>
                        <th className="text-right py-2 px-3 text-gray-400 font-medium">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanList.map((loan, idx) => (
                        <tr key={loan.id || idx} className={`border-b border-[#222] ${idx % 2 === 0 ? 'bg-[#0d0d0d]' : ''}`}>
                          <td className="py-2.5 px-3 text-white font-medium">{loan.clientName}</td>
                          <td className="py-2.5 px-3 text-gray-300">{loan.lender || '—'}</td>
                          <td className="py-2.5 px-3 text-white text-right">{formatCurrency(loan.loanAmount)}</td>
                          <td className="py-2.5 px-3 text-gray-300 text-center">{formatDate(loan.date)}</td>
                          <td className={`py-2.5 px-3 text-right font-medium ${isSelfGen ? 'text-green-400' : 'text-blue-400'}`}>{formatCurrency(loan.commission)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-[#444]">
                        <td className="py-2.5 px-3 text-white font-semibold" colSpan="2">Total ({loanList.length} loans)</td>
                        <td className="py-2.5 px-3 text-white text-right font-semibold">{formatCurrency(loanList.reduce((sum, l) => sum + (l.loanAmount || 0), 0))}</td>
                        <td></td>
                        <td className={`py-2.5 px-3 text-right font-semibold ${isSelfGen ? 'text-green-400' : 'text-blue-400'}`}>{formatCurrency(periodData?.earnings || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No loans found for this period
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Aged Loans Dialog (Admin viewing processor) */}
      <Dialog open={showAgedLoansDialog} onOpenChange={setShowAgedLoansDialog}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-3xl w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Aged Loans — 25+ Days in Process
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {agedLoansData.length} loan{agedLoansData.length !== 1 ? 's' : ''} have been in process for over 25 days
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
            {agedLoansData.map(loan => (
              <div key={loan.id} className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold">{loan.clientName}</span>
                  <Badge className="bg-red-900/50 text-red-400 border border-red-800">{loan.daysAged}d aged</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">LO:</span> <span className="text-gray-300">{loan.userName}</span></div>
                  <div><span className="text-gray-500">Lender:</span> <span className="text-gray-300">{loan.lender || '—'}</span></div>
                  <div><span className="text-gray-500">Amount:</span> <span className="text-gray-300">{formatCurrency(loan.loanAmount)}</span></div>
                  <div><span className="text-gray-500">Loan #:</span> <span className="text-gray-300">{loan.loanNumber}</span></div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Status Picker Dialog */}
      <Dialog open={statusPickerDialog.open} onOpenChange={(open) => !open && setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' })}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Update Loan Status</DialogTitle>
            <DialogDescription className="text-gray-400">Select a new status for this loan</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            {['In Process', 'Closed', 'Withdrawn'].map(status => {
              const isActive = statusPickerDialog.currentStatus === status || 
                (statusPickerDialog.currentStatus === 'Pending' && status === 'In Process');
              return (
                <Button
                  key={status}
                  variant="outline"
                  data-testid={`dash-status-option-${status.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => handleDashboardStatusSelect(status)}
                  className={`justify-start h-12 text-sm font-medium transition-all ${
                    status === 'In Process'
                      ? isActive 
                        ? 'bg-amber-900/50 text-amber-400 border-amber-600 ring-2 ring-amber-500/30'
                        : 'bg-amber-900/20 text-amber-400 border-amber-800 hover:bg-amber-900/40'
                    : status === 'Closed'
                      ? isActive
                        ? 'bg-green-900/50 text-green-400 border-green-600 ring-2 ring-green-500/30'
                        : 'bg-green-900/20 text-green-400 border-green-800 hover:bg-green-900/40'
                    : isActive
                        ? 'bg-red-900/50 text-red-400 border-red-600 ring-2 ring-red-500/30'
                        : 'bg-red-900/20 text-red-400 border-red-800 hover:bg-red-900/40'
                  }`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full mr-3 ${
                    status === 'In Process' ? 'bg-amber-400' : status === 'Closed' ? 'bg-green-400' : 'bg-red-400'
                  }`} />
                  {status}
                  {isActive && <span className="ml-auto text-xs opacity-60">Current</span>}
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Closing & Funding Dialog (Admin) */}
      <Dialog open={fundingDateDialog.open} onOpenChange={(open) => {
        if (!open) setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' });
      }}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Closing & Funding</DialogTitle>
            <DialogDescription className="text-gray-400">
              Confirm the closing date and enter the funding date to mark this loan as closed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dashClosingDate2" className="text-gray-300">Closing Date</Label>
              <Input
                id="dashClosingDate2"
                type="date"
                data-testid="dash-closing-date-input"
                value={fundingDateDialog.closingDate}
                onChange={(e) => setFundingDateDialog({ ...fundingDateDialog, closingDate: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dashFundingDate2" className="text-gray-300">Funding Date</Label>
              <Input
                id="dashFundingDate2"
                type="date"
                data-testid="dash-funding-date-input"
                value={fundingDateDialog.fundingDate}
                onChange={(e) => setFundingDateDialog({ ...fundingDateDialog, fundingDate: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={handleDashboardFundingSubmit}
              data-testid="dash-mark-as-closed-btn"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              Mark as Closed
            </Button>
            <Button
              variant="outline"
              onClick={() => setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' })}
              className="border-[#333] text-gray-300 hover:bg-[#1a1a1a]"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
